import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Copy, Users, Activity, Loader, AlertCircle, Wallet, Plus, Settings, Play, Pause, Trash2, Edit2, ToggleLeft, ToggleRight, History, TrendingUp, Info, Check, Search, ExternalLink, Globe, MessageCircle, Youtube, Pill, X, RefreshCw, Target } from 'lucide-react';
import { globalWidgetTextStyles, widgetClassName } from '../../styles/globalWidgetStyles';
import { wsService } from '../../services/websocketService';
import { useWalletUser } from '../../hooks/useWalletUser';
import { getUserTradingWallets } from '../../services/tradingWalletService';
import { getBalance } from '../../services/shyftService';
import { getTokenBalance } from '../../services/solanatrackerService';
import { tradeEngine } from '../../services/tradeEngine';
import { adminSupabase } from '../../services/supabaseService';
import { userService } from '../../services/userService';
import { createAdvancedChartsWidget } from '../AdvancedCharts/AdvancedCharts';
import { createTokenInfoWidget } from '../TokenInfo/TokenInfo';
import { Token } from '../../types/supabase';
import { CopyPoolSubscriber, CopyTrade } from '../../types/tradingBot';
import { ensureCopyTradesTable } from '../../utils/createCopyTradesTable';
import { copyTradeService } from '../../services/copyTradeService';
import { CopyTradeRule, LocalCopyTraderRule, CreateCopyTradeRuleRequest, CopyTradeRuleStatistics } from '../../types/copyTrader';

// Types for copy trading rules - using imported LocalCopyTraderRule from types/copyTrader.ts

interface TradingWallet {
  id: string;
  name: string;
  public_key: string;
  created_at: string;
  owner_wallet_address: string;
  is_active: boolean;
  last_used_at?: string;
  balance?: number;
}

interface SelectedWallet extends TradingWallet {
  solBalance: number;
  isSelected: boolean;
}

// Define the props for the CopyTrader component
export interface CopyTraderProps {
  onClose?: () => void;
  isWidget?: boolean;
  onAddWidget?: (widget: any) => void; // For adding widgets to canvas
}

const CopyTrader: React.FC<CopyTraderProps> = ({
  onClose,
  isWidget = false,
  onAddWidget
}) => {
  const walletUser = useWalletUser();
  
  // Custom notification functions to replace react-hot-toast
  const showNotification = (message: string, type: 'success' | 'error' | 'loading', id?: string) => {
    const toastId = id || `copytrader-${type}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    
    window.dispatchEvent(new CustomEvent('ekko:show_toast', {
      detail: {
        message,
        type,
        id: toastId
      }
    }));
    
    // Auto-hide success notifications after 3 seconds
    if (type === 'success') {
      setTimeout(() => {
        hideNotification(toastId, 'success');
      }, 3000);
    }
    
    return toastId;
  };

  const hideNotification = (id: string, type?: 'success' | 'error' | 'loading') => {
    window.dispatchEvent(new CustomEvent('ekko:hide_toast', {
      detail: {
        id,
        type
      }
    }));
  };

  const updateNotification = (id: string, message: string, type: 'success' | 'error' | 'loading') => {
    window.dispatchEvent(new CustomEvent('ekko:update_toast', {
      detail: {
        id,
        message,
        type
      }
    }));
  };

  // Helper function to get effective wallet address (use direct connection)
  const getEffectiveWalletAddress = (): string | null => {
    // Use direct wallet connection since it's always reliable
    if (typeof window !== 'undefined' && window.solana?.publicKey) {
      return window.solana.publicKey.toString();
    }
    
    // Fallback to hook only if direct connection fails
    if (walletUser.walletAddress && walletUser.walletAddress.trim() !== '') {
      console.warn('⚠️ Using hook as fallback (direct wallet unavailable):', walletUser.walletAddress);
      return walletUser.walletAddress;
    }
    
    return null;
  };
  
  // State management
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected' | 'error'>('disconnected');
  const [isLoading, setIsLoading] = useState(false);
  const subscribedWalletsRef = useRef<Set<string>>(new Set());
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const walletSubscriptionsRef = useRef<Map<string, (() => void)[]>>(new Map());

  // Trading wallet management
  const [tradingWallets, setTradingWallets] = useState<TradingWallet[]>([]);
  const [selectedWallets, setSelectedWallets] = useState<Set<string>>(new Set());
  
  // Debug wrapper for setSelectedWallets to track changes
  const setSelectedWalletsWithDebug = (wallets: Set<string> | ((prev: Set<string>) => Set<string>)) => {
    if (typeof wallets === 'function') {
      setSelectedWallets(prev => {
        const newWallets = wallets(prev);
        console.log(`🔄 selectedWallets updated (function):`, {
          from: Array.from(prev),
          to: Array.from(newWallets),
          stack: new Error().stack?.split('\n').slice(1, 4).join('\n')
        });
        return newWallets;
      });
    } else {
      setSelectedWallets(prev => {
        console.log(`🔄 selectedWallets updated (direct):`, {
          from: Array.from(prev),
          to: Array.from(wallets),
          stack: new Error().stack?.split('\n').slice(1, 4).join('\n')
        });
        return wallets;
      });
    }
  };
  const [walletBalances, setWalletBalances] = useState<Map<string, number>>(new Map());
  const [isLoadingWallets, setIsLoadingWallets] = useState(false);

  // Copy trader configuration
  const [solAmountPerTrade, setSolAmountPerTrade] = useState<string>('0.1');
  const [maxTradesToCopy, setMaxTradesToCopy] = useState<string>('10');
  const [slippagePercent, setSlippagePercent] = useState<string>('35');
  const [copyDelaySeconds, setCopyDelaySeconds] = useState<string>('2');
  const [tradeSizeType, setTradeSizeType] = useState<'fixed' | 'percentage'>('fixed');
  const [tradeSizeValue, setTradeSizeValue] = useState<string>('0.1');
  const [copyBuys, setCopyBuys] = useState(true);
  const [copySells, setCopySells] = useState(true);
  const [isCopyingActive, setIsCopyingActive] = useState(false);
  const isCopyingActiveRef = useRef<boolean>(false);

  // Rules management
  const [copyTraderRules, setCopyTraderRules] = useState<LocalCopyTraderRule[]>([]);
  const copyTraderRulesRef = useRef<LocalCopyTraderRule[]>([]);
  const [isLoadingRules, setIsLoadingRules] = useState(false);

  // Current view state
  const [activeTab, setActiveTab] = useState<'monitor' | 'transactions'>('monitor');
  
  // Copy trader bot creation flow
  const [isCreatingBot, setIsCreatingBot] = useState(false);
  const [creationStep, setCreationStep] = useState<'wallet' | 'config'>('wallet');
  const [newBotName, setNewBotName] = useState('');
  const [sourceWalletAddress, setSourceWalletAddress] = useState('');
  const [sourceWalletName, setSourceWalletName] = useState('');
  const [editingBotId, setEditingBotId] = useState<string | null>(null); // Track if we're editing

  // Deletion confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<{ id: string; name: string; isActive: boolean } | null>(null);

  // Performance optimization refs
  const maxTrackedWallets = 20; // Limit tracked wallets
  const cleanupThreshold = 18; // Start cleanup before hitting the limit
  const walletsToRemoveCount = 5; // Remove first 5 wallets for efficiency

  // Copy trade tracking per rule - SEPARATE BUY/SELL TRACKING
  const ruleBuyCountRef = useRef<Map<string, number>>(new Map());
  const ruleSellCountRef = useRef<Map<string, number>>(new Map());
  
  // Track recently copied transactions to prevent duplicates (txSignature -> timestamp)
  const recentlyCopiedTransactionsRef = useRef<Map<string, number>>(new Map());
  const DUPLICATE_PREVENTION_TIME = 60000; // 1 minute
  
  // Track wallet rotation for round-robin distribution (ruleId -> walletIndex)
  const walletRotationRef = useRef<Map<string, number>>(new Map());
  
  // Track executing trades per rule to prevent race conditions (ruleId -> Promise)
  const ruleExecutionLockRef = useRef<Map<string, Promise<void>>>(new Map());

  // Trade history management
  const [tradeHistory, setTradeHistory] = useState<any[]>([]);
  const [isLoadingTradeHistory, setIsLoadingTradeHistory] = useState(false);
  const realtimeSubscriptionRef = useRef<any>(null);

  // Rule statistics (ruleId -> CopyTradeRuleStatistics)
  const [ruleStatistics, setRuleStatistics] = useState<Map<string, CopyTradeRuleStatistics>>(new Map());

  // Copy to clipboard state
  const [copiedWallets, setCopiedWallets] = useState<Set<string>>(new Set());

  // Token data cache for transactions - USE REF to prevent re-renders (like SniperBot)
  const tokenDataCacheRef = useRef<Map<string, any>>(new Map());
  const loadingTokenDataRef = useRef<Set<string>>(new Set());

  // Fetch token data from Solana Tracker API (exactly like SniperBot)
  const fetchTokenData = async (tokenAddress: string) => {
    // Check if already cached
    if (tokenDataCacheRef.current.has(tokenAddress)) {
      return tokenDataCacheRef.current.get(tokenAddress);
    }

    // Check if already loading
    if (loadingTokenDataRef.current.has(tokenAddress)) {
      return null;
    }

    try {
      loadingTokenDataRef.current.add(tokenAddress);

      const apiKey = import.meta.env.VITE_SOLANA_TRACKER_API_KEY;
      if (!apiKey) {
        console.error('Solana Tracker API key not found');
        return null;
      }

      const response = await fetch(`https://data.solanatracker.io/tokens/${tokenAddress}`, {
        headers: {
          'x-api-key': apiKey,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Cache the data
      tokenDataCacheRef.current.set(tokenAddress, data);
      
      console.log(`✅ Copy Trader: Fetched token data for ${tokenAddress}:`, data.token?.symbol || 'Unknown');
      return data;
    } catch (error) {
      console.error(`Error fetching token data for ${tokenAddress}:`, error);
      return null;
    } finally {
      loadingTokenDataRef.current.delete(tokenAddress);
    }
  };

  // Copy wallet address to clipboard
  const copyWalletAddress = async (walletAddress: string) => {
    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopiedWallets(prev => new Set([...prev, walletAddress]));
      
      // Remove from copied set after 2 seconds
      setTimeout(() => {
        setCopiedWallets(prev => {
          const newSet = new Set(prev);
          newSet.delete(walletAddress);
          return newSet;
        });
      }, 2000);
      
      showNotification(`Wallet address copied to clipboard!`, 'success');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      showNotification(`Failed to copy address`, 'error');
    }
  };

  // Render social links for token (matching SniperBot styling exactly)
  const renderTokenSocials = (tokenData: any, tokenAddress: string, tokenSymbol: string) => {
    if (!tokenData?.token) return null;

    const { token } = tokenData;
    const socials = token.strictSocials || {};
    
    return (
      <div className="mb-2">
        {/* Social Links - Exact SniperBot styling */}
        <div className="flex gap-1">
          {/* X.com Search */}
          <a
            href={`https://x.com/search?q=${tokenSymbol}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-black rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
            title="Search on Twitter"
          >
            <Search size={10} className="text-white" />
          </a>

          {/* Pump.fun pill */}
          <a
            href={`https://pump.fun/coin/${tokenAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-black rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
            title="View on Pump.fun"
          >
            <Pill size={10} className="text-white" />
          </a>

          {/* Twitter/X Official */}
          {socials.twitter && (
            <a
              href={socials.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
              title="Official Twitter"
            >
              <img 
                src="https://cdn.cms-twdigitalassets.com/content/dam/about-twitter/x/brand-toolkit/logo-black.png.twimg.2560.png" 
                alt="X" 
                className="w-2.5 h-2.5 invert"
              />
            </a>
          )}

          {/* Telegram */}
          {socials.telegram && (
            <a
              href={socials.telegram}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
              title="Official Telegram"
            >
              <MessageCircle size={10} className="text-blue-400" />
            </a>
          )}

          {/* Website */}
          {socials.website && (
            <a
              href={socials.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
              title="Official Website"
            >
              <Globe size={10} className="text-gray-300" />
            </a>
          )}

          {/* YouTube */}
          {socials.youtube && (
            <a
              href={socials.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
              title="Official YouTube"
            >
              <Youtube size={10} className="text-red-400" />
            </a>
          )}

          {/* Solscan */}
          <a
            href={`https://solscan.io/token/${tokenAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-black rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
            title="View on Solscan"
          >
            <ExternalLink size={10} className="text-white" />
          </a>
        </div>
      </div>
    );
  };

  // Memoized Token info component for transactions to prevent re-renders (exactly like SniperBot)
  const TokenInfoDisplay = React.memo<{ tokenAddress: string; tokenSymbol: string }>(({ tokenAddress, tokenSymbol }) => {
    const [tokenData, setTokenData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
      const loadTokenData = async () => {
        if (tokenDataCacheRef.current.has(tokenAddress)) {
          setTokenData(tokenDataCacheRef.current.get(tokenAddress));
          return;
        }

        setIsLoading(true);
        const data = await fetchTokenData(tokenAddress);
        if (data) {
          setTokenData(data);
        }
        setIsLoading(false);
      };

      loadTokenData();
    }, [tokenAddress]);

    if (isLoading) {
      return (
        <div className="flex items-center gap-1 mb-2">
          <Loader size={10} className="text-gray-400 animate-spin" />
          <span className="text-gray-400 text-[10px]">Loading token info...</span>
        </div>
      );
    }

    if (!tokenData) {
      return null;
    }

    return renderTokenSocials(tokenData, tokenAddress, tokenSymbol);
  });

  // Load trade history when transactions tab is selected and set up real-time subscription
  useEffect(() => {
    if (activeTab === 'transactions') {
      loadTradeHistory();
      
      // Set up real-time subscription for copy trades
      const effectiveWalletAddress = getEffectiveWalletAddress();
      if (effectiveWalletAddress) {
        const unsubscribe = copyTradeService.subscribeToTradeUpdates(
          effectiveWalletAddress,
          (trade) => {
            console.log(`📨 Real-time copy trade update received:`, trade);
            // Add the new trade to the beginning of the list
            setTradeHistory(prev => [trade, ...prev.slice(0, 49)]); // Keep only 50 most recent
          }
        );
        
        return unsubscribe;
      }
    }
  }, [activeTab]);

  // Memoized Timer component for transactions to prevent re-renders
  const TransactionTimer = React.memo<{ createdAt: string }>(({ createdAt }) => {
    const [elapsed, setElapsed] = useState('');

    useEffect(() => {
      const updateElapsed = () => {
        const now = new Date().getTime();
        const created = new Date(createdAt).getTime();
        const diff = now - created;

        if (diff < 0) {
          setElapsed('now');
          return;
        }

        const seconds = Math.floor(diff / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) {
          setElapsed(`${days}d`);
        } else if (hours > 0) {
          setElapsed(`${hours}h`);
        } else if (minutes > 0) {
          setElapsed(`${minutes}m`);
        } else {
          setElapsed(`${seconds}s`);
        }
      };

      updateElapsed();
      const interval = setInterval(updateElapsed, 1000);

      return () => clearInterval(interval);
    }, [createdAt]);

    return (
      <div className="text-[#24c35b] text-xs font-bold text-center">
        {elapsed}
      </div>
    );
  });

  // Functions to open widgets with specific tokens
  const openAdvancedChartsWidget = (tokenAddress: string, tokenSymbol: string, tokenName?: string) => {
    // Create a properly formed token object with default values
    const tokenData: Token = {
      // Essential identification fields
      mint: tokenAddress,
      symbol: tokenSymbol,
      name: tokenName || tokenSymbol,
      id: tokenAddress,

      // Default values for required fields (will be updated by the chart widget)
      signature: '',
      trader_public_key: '',
      tx_type: 'buy',
      sol_amount: 0,
      token_amount: 0,
      timestamp: new Date().toISOString(),
      price_usd: 0,
      market_cap_usd: 0,
      liquidity_usd: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),

      // Additional fields that might be needed
      initial_buy: null,
      market_cap_sol: null,
      bonding_curve_key: null,
      v_sol_in_bonding_curve: null,
      v_tokens_in_bonding_curve: null,
      uri: null,
      metadata: null,
      description: null,
      image_uri: null,
      image_url: null,
      twitter: null,
      telegram: null,
      website: null,
      discord: null,
      attributes: null,
      dev_projects: null,
      is_rugged: null,
      rug_date: null,
      rug_type: null,
      buys: null,
      sells: null,
      holders: null,
      volume: null,
      liquidity: null,
      token_price: null,
      snipers: null,
      insiders: null,
      top_10_holders: null,
      bonding_curve_progress: null,
      sequence_number: null,
      volume_usd: null,
      tx_volume: null,
      buys_count: null,
      sells_count: null,
      holders_count: null,
      position: null,
      market_type: null,
      market: null,
      youtube: null,
      is_raydium: undefined,
      is_migrated: undefined,
      first_seen_in_trending: null,
      program: null,
      transaction_time: null,
      transaction_type: null,
      transaction_amount: null,
      transaction_price_usd: null,
      sol_volume: null,
      volume_5m: null,
      volume_15m: null,
      volume_30m: null,
      volume_1h: null,
      volume_6h: null,
      volume_12h: null,
      volume_24h: null,
      jupiter: null,
      risk: undefined,
      events: undefined,
      pools: undefined,
      dev_wallet: null,
      dev_buy_tokens: null,
      dev_buy_time: null,
      dev_sell_time: null
    };

    if (onAddWidget) {
      const chartWidget = createAdvancedChartsWidget(tokenData);
      onAddWidget(chartWidget);
    }
  };

  const openTokenInfoWidget = (tokenAddress: string, tokenSymbol: string, tokenName?: string) => {
    const tokenData: Token = {
      // Essential identification fields
      mint: tokenAddress,
      symbol: tokenSymbol,
      name: tokenName || tokenSymbol,
      id: tokenAddress,

      // Default values for required fields
      signature: '',
      trader_public_key: '',
      tx_type: 'buy',
      sol_amount: 0,
      token_amount: 0,
      timestamp: new Date().toISOString(),
      price_usd: 0,
      market_cap_usd: 0,
      liquidity_usd: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),

      // Additional fields
      initial_buy: null,
      market_cap_sol: null,
      bonding_curve_key: null,
      v_sol_in_bonding_curve: null,
      v_tokens_in_bonding_curve: null,
      uri: null,
      metadata: null,
      description: null,
      image_uri: null,
      image_url: null,
      twitter: null,
      telegram: null,
      website: null,
      discord: null,
      attributes: null,
      dev_projects: null,
      is_rugged: null,
      rug_date: null,
      rug_type: null,
      buys: null,
      sells: null,
      holders: null,
      volume: null,
      liquidity: null,
      token_price: null,
      snipers: null,
      insiders: null,
      top_10_holders: null,
      bonding_curve_progress: null,
      sequence_number: null,
      volume_usd: null,
      tx_volume: null,
      buys_count: null,
      sells_count: null,
      holders_count: null,
      position: null,
      market_type: null,
      market: null,
      youtube: null,
      is_raydium: undefined,
      is_migrated: undefined,
      first_seen_in_trending: null,
      program: null,
      transaction_time: null,
      transaction_type: null,
      transaction_amount: null,
      transaction_price_usd: null,
      sol_volume: null,
      volume_5m: null,
      volume_15m: null,
      volume_30m: null,
      volume_1h: null,
      volume_6h: null,
      volume_12h: null,
      volume_24h: null,
      jupiter: null,
      risk: undefined,
      events: undefined,
      pools: undefined,
      dev_wallet: null,
      dev_buy_tokens: null,
      dev_buy_time: null,
      dev_sell_time: null
    };

    if (onAddWidget) {
      const infoWidget = createTokenInfoWidget({ selectedToken: tokenData });
      onAddWidget(infoWidget);
    }
  };

  // Execute copy trade
  const executeCopyTrade = async (
    ruleId: string,
    sourceWalletAddress: string,
    transactionData: any,
    ruleConfig: {
      solAmountPerTrade: number;
      slippagePercent: number;
      maxTradesToCopy: number;
      tradingWalletAddresses: string[];
      tradeSizeType: 'fixed' | 'percentage';
      tradeSizeValue: string;
      copyDelaySeconds: number;
    },
    appliedRuleName: string
  ) => {
    // ENHANCED VALIDATION: Validate input parameters
    if (!ruleId || typeof ruleId !== 'string') {
      console.error(`❌ Invalid rule ID: ${ruleId}`);
      return;
    }
    
    if (!sourceWalletAddress || typeof sourceWalletAddress !== 'string') {
      console.error(`❌ Invalid source wallet address: ${sourceWalletAddress}`);
      return;
    }
    
    if (!transactionData || typeof transactionData !== 'object') {
      console.error(`❌ Invalid transaction data: not an object`);
      return;
    }
    
    if (!transactionData.tx || typeof transactionData.tx !== 'string') {
      console.error(`❌ Invalid transaction signature: ${transactionData.tx}`);
      return;
    }
    
    if (!transactionData.type || (transactionData.type !== 'buy' && transactionData.type !== 'sell')) {
      console.error(`❌ Invalid transaction type: ${transactionData.type}`);
      return;
    }
    
    // ENHANCED VALIDATION: Validate rule configuration
    if (!ruleConfig || typeof ruleConfig !== 'object') {
      console.error(`❌ Invalid rule configuration: not an object`);
      return;
    }
    
    if (isNaN(ruleConfig.solAmountPerTrade) || ruleConfig.solAmountPerTrade <= 0) {
      console.error(`❌ Invalid SOL amount per trade: ${ruleConfig.solAmountPerTrade}`);
      return;
    }
    
    if (isNaN(ruleConfig.slippagePercent) || ruleConfig.slippagePercent < 0 || ruleConfig.slippagePercent > 100) {
      console.error(`❌ Invalid slippage percent: ${ruleConfig.slippagePercent}`);
      return;
    }
    
    if (!Array.isArray(ruleConfig.tradingWalletAddresses) || ruleConfig.tradingWalletAddresses.length === 0) {
      console.error(`❌ No valid trading wallet addresses configured`);
      return;
    }
    
    if (isNaN(ruleConfig.copyDelaySeconds) || ruleConfig.copyDelaySeconds < 0) {
      console.error(`❌ Invalid copy delay seconds: ${ruleConfig.copyDelaySeconds}`);
      return;
    }
    
    console.log(`🔍 COPY TRADE VALIDATION PASSED: Rule "${appliedRuleName}" for ${transactionData.type} transaction`);
    
    // Check if we've already copied this transaction
    if (recentlyCopiedTransactionsRef.current.has(transactionData.tx)) {
      console.log(`⏭️ Skipping duplicate transaction: ${transactionData.tx}`);
      return;
    }

    // Add to recent copied transactions
    recentlyCopiedTransactionsRef.current.set(transactionData.tx, Date.now());

    // Cleanup old entries
    const now = Date.now();
    for (const [txSig, timestamp] of recentlyCopiedTransactionsRef.current.entries()) {
      if (now - timestamp > DUPLICATE_PREVENTION_TIME) {
        recentlyCopiedTransactionsRef.current.delete(txSig);
      }
    }

    // Check if we've hit the max BUY trades limit for this rule (only applies to BUY transactions)
    if (transactionData.type === 'buy') {
      const currentBuyCount = ruleBuyCountRef.current.get(ruleId) || 0;
      if (currentBuyCount >= ruleConfig.maxTradesToCopy) {
        console.log(`⚠️ Rule ${appliedRuleName} has reached max BUY trades limit (${currentBuyCount}/${ruleConfig.maxTradesToCopy})`);
        return;
      }
    }
    
    // SELL transactions are NOT limited by maxTradesToCopy - they can always execute
    if (transactionData.type === 'sell') {
      console.log(`✅ SELL transaction - no trade limit applies (sells are unlimited)`);
    }

    // Prevent race conditions by checking if a trade is already executing for this rule
    if (ruleExecutionLockRef.current.has(ruleId)) {
      console.log(`⏳ Trade already executing for rule ${appliedRuleName}, skipping...`);
      return;
    }

    // Add delay if specified
    if (ruleConfig.copyDelaySeconds > 0) {
      console.log(`⏱️ Delaying copy trade by ${ruleConfig.copyDelaySeconds} seconds...`);
      await new Promise(resolve => setTimeout(resolve, ruleConfig.copyDelaySeconds * 1000));
    }

    const executionPromise = async () => {
      // Increment appropriate counter based on transaction type
      if (transactionData.type === 'buy') {
        const currentBuyCount = ruleBuyCountRef.current.get(ruleId) || 0;
        ruleBuyCountRef.current.set(ruleId, currentBuyCount + 1);
        console.log(`📊 Incremented BUY count for rule ${appliedRuleName}: ${currentBuyCount + 1}/${ruleConfig.maxTradesToCopy}`);
      } else if (transactionData.type === 'sell') {
        const currentSellCount = ruleSellCountRef.current.get(ruleId) || 0;
        ruleSellCountRef.current.set(ruleId, currentSellCount + 1);
        console.log(`📊 Incremented SELL count for rule ${appliedRuleName}: ${currentSellCount + 1} (unlimited)`);
      }

      try {
        // Get the actual user wallet address (not the source wallet)
        const actualUserWalletAddress = getEffectiveWalletAddress();
        if (!actualUserWalletAddress) {
          throw new Error('No user wallet address available');
        }

        // Execute trades from ALL configured wallets in the rule (like Sniper Bot)
        const configuredWallets = ruleConfig.tradingWalletAddresses;

        if (!configuredWallets || configuredWallets.length === 0) {
          throw new Error('No trading wallets configured for this copy trade rule');
        }

        // Verify that the configured wallets actually exist in our trading wallets list
        const validWallets = configuredWallets.filter(addr => 
          tradingWallets.some(w => w.public_key === addr)
        );

        if (validWallets.length === 0) {
          throw new Error('None of the configured trading wallets are available');
        }

        console.log(`🎯 Executing copy trade from ${validWallets.length} wallet(s):`, validWallets.map(addr => addr.slice(0, 8) + '...'));

        // Calculate trade amount based on trade size type
        let tradeAmount: number;
        let isAllTokenSell = false; // Flag to indicate we want to sell all tokens
        
        if (transactionData.type === 'sell') {
          console.log(`🔍 SELL TRANSACTION: Will sell 100% of token balance (copy trader behavior)`);
          
          // For SELL transactions in copy trading, ALWAYS sell 100% of the owned tokens
          // We'll use a special value and flag to indicate this to the trade engine
          tradeAmount = -1; // Special flag for "sell all tokens"
          isAllTokenSell = true;
          
          console.log(`📊 SELL MODE: Will sell ALL tokens when trade executes`);
        } else {
          // BUY transaction logic (existing logic works fine)
          if (ruleConfig.tradeSizeType === 'percentage') {
            // For percentage, calculate based on source transaction volume
            const sourceVolume = transactionData.volume?.sol || transactionData.solVolume || 0;
            
            // ENHANCED VALIDATION: Validate source volume
            if (isNaN(sourceVolume) || sourceVolume <= 0) {
              console.warn(`⚠️ Invalid source volume for percentage calculation: ${sourceVolume}, using fixed amount instead`);
              tradeAmount = parseFloat(ruleConfig.tradeSizeValue);
            } else {
              const percentage = parseFloat(ruleConfig.tradeSizeValue) / 100;
              if (isNaN(percentage) || percentage <= 0) {
                throw new Error(`Invalid percentage value: ${ruleConfig.tradeSizeValue}`);
              }
              tradeAmount = sourceVolume * percentage;
            }
          } else {
            // For fixed amount
            tradeAmount = parseFloat(ruleConfig.tradeSizeValue);
          }
          
          // ENHANCED VALIDATION: Validate calculated trade amount for buys
          if (isNaN(tradeAmount) || tradeAmount <= 0) {
            throw new Error(`Invalid calculated trade amount: ${tradeAmount}`);
          }

          // Ensure minimum trade amount for buys
          if (tradeAmount < 0.001) {
            tradeAmount = 0.001;
          }
        }
        
        // Note: For sells, validation will happen in the trade engine when it gets actual token balance

        // Extract token information with enhanced validation
        let tokenAddress = '';
        let tokenSymbol = 'Unknown';
        let tokenName = 'Unknown';

        console.log(`🔍 Extracting token info from transaction data...`);

        // Method 1: For BUY transactions, get token from 'to' object
        if (transactionData.type === 'buy' && transactionData.to && transactionData.to.address) {
          tokenAddress = transactionData.to.address;
          tokenSymbol = transactionData.to.token?.symbol || 'Unknown';
          tokenName = transactionData.to.token?.name || tokenSymbol;
          console.log(`📍 Method 1 (buy - to object):`, { tokenAddress, tokenSymbol, tokenName });
        }
        // Method 2: For SELL transactions, get token from 'from' object
        else if (transactionData.type === 'sell' && transactionData.from && transactionData.from.address) {
          tokenAddress = transactionData.from.address;
          tokenSymbol = transactionData.from.token?.symbol || 'Unknown';
          tokenName = transactionData.from.token?.name || tokenSymbol;
          console.log(`📍 Method 2 (sell - from object):`, { tokenAddress, tokenSymbol, tokenName });
        }
        // Additional extraction methods...
        else if (transactionData.tokenAddress || transactionData.mint || transactionData.token_address) {
          tokenAddress = transactionData.tokenAddress || transactionData.mint || transactionData.token_address || '';
          tokenSymbol = transactionData.tokenSymbol || transactionData.symbol || 'Unknown';
          tokenName = transactionData.tokenName || transactionData.name || tokenSymbol;
          console.log(`📍 Root level extraction:`, { tokenAddress, tokenSymbol, tokenName });
        }
        
        // ENHANCED VALIDATION: Validate extracted token information
        if (!tokenAddress || typeof tokenAddress !== 'string' || tokenAddress.trim() === '') {
          throw new Error(`Invalid token address extracted: ${tokenAddress}`);
        }
        
        // Validate token address is not SOL mint for token trades
        const SOL_MINT = 'So11111111111111111111111111111111111111112';
        if (tokenAddress === SOL_MINT) {
          throw new Error(`Invalid token address: cannot trade SOL as a token (${tokenAddress})`);
        }

        // For sell trades, add a warning but let the trade engine handle it
        if (transactionData.type === 'sell') {
          console.log(`⚠️ Copy trader attempting to sell ${tokenSymbol}. If you don't own this token, the trade will fail gracefully.`);
        }

        // Execute trades for ALL valid wallets (like Sniper Bot)
        let successCount = 0;
        let failCount = 0;
        const toastIds = new Map<string, string>(); // Track toast IDs per wallet

        for (const walletAddress of validWallets) {
          let toastId: string | undefined;
          
          try {
            console.log(`🎯 Executing copy trade:`, {
              rule: appliedRuleName,
              sourceWallet: sourceWalletAddress,
              tradingWallet: walletAddress,
              token: `${tokenSymbol} (${tokenAddress})`,
              type: transactionData.type,
              amount: transactionData.type === 'sell' ? 'ALL TOKENS' : `${tradeAmount} SOL`,
              slippage: `${ruleConfig.slippagePercent}%`
            });

            // Show loading notification for this wallet
            const notificationMessage = transactionData.type === 'sell' 
              ? `Copying sell of ALL ${tokenSymbol} tokens from ${walletAddress.slice(0, 8)}...`
              : `Copying ${transactionData.type} of ${tokenSymbol} (${tradeAmount} SOL) from ${walletAddress.slice(0, 8)}...`;
            
            toastId = showNotification(notificationMessage, 'loading') as string;
            toastIds.set(walletAddress, toastId);

            // Validate trade parameters before execution
            const inputMint = transactionData.type === 'buy' ? SOL_MINT : tokenAddress;
            const outputMint = transactionData.type === 'buy' ? tokenAddress : SOL_MINT;

            // Double-check to prevent circular arbitrage
            if (inputMint === outputMint) {
              throw new Error(`Invalid trade: input and output mints are the same (${inputMint}). This would cause circular arbitrage.`);
            }

            console.log(`🔄 Executing ${transactionData.type} trade:`, {
              inputMint,
              outputMint,
              amount: tradeAmount,
              tokenAddress,
              tokenSymbol,
              wallet: walletAddress.slice(0, 8) + '...',
              // SLIPPAGE DEBUG - What we're sending to trade engine
              slippagePercent: ruleConfig.slippagePercent,
              slippagePercentString: ruleConfig.slippagePercent.toString()
            });

            // Execute the trade using the trade engine (it handles keypair restoration internally)
            const tradeResult = await tradeEngine.executeSwap({
              inputMint,
              outputMint,
              amount: tradeAmount.toString(),
              userWalletAddress: actualUserWalletAddress, // Use the actual user's wallet, not the source wallet
              tradingWalletAddress: walletAddress,
              slippagePercent: ruleConfig.slippagePercent.toString(),
              tradeContext: {
                isCopyTrader: true,
                ruleName: appliedRuleName,
                ruleId: ruleId,
                metadata: {
                  sourceWallet: sourceWalletAddress,
                  sourceTransaction: transactionData.tx,
                  isAllTokenSell: isAllTokenSell // Flag for selling all tokens
                }
              }
            });

            if (tradeResult.signature) {
              console.log(`✅ COPY TRADE SUCCESS! Wallet: ${walletAddress.slice(0, 8)}... | Signature: ${tradeResult.signature}`);
              successCount++;

              // Log the copy trade to the copy_trades table
              await logCopyTrade({
                ruleId: ruleId,
                ruleName: appliedRuleName,
                userWalletAddress: actualUserWalletAddress,
                tradingWalletAddress: walletAddress,
                sourceWallet: sourceWalletAddress,
                sourceTransactionSignature: transactionData.tx,
                sourceTransactionData: transactionData,
                transactionType: transactionData.type,
                tokenAddress: tokenAddress,
                tokenSymbol: tokenSymbol,
                tokenName: tokenName,
                amount: tradeAmount,
                slippagePercent: ruleConfig.slippagePercent,
                copyDelayApplied: ruleConfig.copyDelaySeconds,
                transactionSignature: tradeResult.signature,
                status: 'success'
              });

              // Update loading notification to success
              const successMessage = transactionData.type === 'sell'
                ? `✅ Successfully sold ALL ${tokenSymbol} tokens from ${walletAddress.slice(0, 8)}...`
                : `✅ Successfully copied ${transactionData.type} of ${tokenSymbol} from ${walletAddress.slice(0, 8)}...`;
              
              updateNotification(toastId, successMessage, 'success');

              showCopyTraderNotification(
                'success',
                appliedRuleName,
                tokenSymbol,
                tokenName,
                transactionData.type === 'sell' ? 0 : tradeAmount, // For sells, amount is not meaningful
                walletAddress,
                transactionData.type,
                tradeResult.signature,
                undefined,
                sourceWalletAddress
              );

            } else {
              throw new Error('Trade execution failed - no signature returned');
            }

          } catch (error) {
            console.error(`❌ Copy trade failed for wallet ${walletAddress.slice(0, 8)}...:`, error);
            failCount++;

            // Log the failed copy trade to the copy_trades table
            await logCopyTrade({
              ruleId: ruleId,
              ruleName: appliedRuleName,
              userWalletAddress: actualUserWalletAddress,
              tradingWalletAddress: walletAddress,
              sourceWallet: sourceWalletAddress,
              sourceTransactionSignature: transactionData.tx,
              sourceTransactionData: transactionData,
              transactionType: transactionData.type,
              tokenAddress: tokenAddress,
              tokenSymbol: tokenSymbol,
              tokenName: tokenName,
              amount: tradeAmount,
              slippagePercent: ruleConfig.slippagePercent,
              copyDelayApplied: ruleConfig.copyDelaySeconds,
              status: 'failed',
              errorMessage: error instanceof Error ? error.message : 'Unknown error'
            });

            // Dismiss loading notification if it exists
            if (toastId) {
              hideNotification(toastId, 'loading');
            }
          }
        }

        // Update rule statistics based on total results
        setRuleStatistics(prev => {
          const newStats = new Map(prev);
          const currentStats = newStats.get(ruleId) || { 
            rule_id: ruleId,
            total_trades: 0, 
            successful_trades: 0, 
            failed_trades: 0,
            success_rate: 0,
            total_volume_sol: 0,
            total_profit_loss_sol: 0,
            average_trade_size_sol: 0
          };
          const newTotalTrades = currentStats.total_trades + successCount + failCount;
          const newSuccessfulTrades = currentStats.successful_trades + successCount;
          const newFailedTrades = currentStats.failed_trades + failCount;
          newStats.set(ruleId, {
            ...currentStats,
            total_trades: newTotalTrades,
            successful_trades: newSuccessfulTrades,
            failed_trades: newFailedTrades,
            success_rate: newTotalTrades > 0 ? (newSuccessfulTrades / newTotalTrades) * 100 : 0
          });
          return newStats;
        });

        console.log(`📊 Copy trade execution completed: ${successCount} successful, ${failCount} failed out of ${validWallets.length} wallets`);

      } catch (error) {
        console.error(`❌ Copy trade execution error:`, error);
        // Decrement appropriate counter since the trade failed
        if (transactionData.type === 'buy') {
          const currentBuyCount = ruleBuyCountRef.current.get(ruleId) || 0;
          ruleBuyCountRef.current.set(ruleId, Math.max(0, currentBuyCount - 1));
        } else if (transactionData.type === 'sell') {
          const currentSellCount = ruleSellCountRef.current.get(ruleId) || 0;
          ruleSellCountRef.current.set(ruleId, Math.max(0, currentSellCount - 1));
        }
      }
    };

    // Set execution lock and execute
    const promise = executionPromise();
    ruleExecutionLockRef.current.set(ruleId, promise);

    try {
      await promise;
    } finally {
      // Remove execution lock
      ruleExecutionLockRef.current.delete(ruleId);
    }
  };

  // Log copy trade using the new service
  const logCopyTrade = async (copyTradeData: {
    ruleId: string;
    ruleName: string;
    userWalletAddress: string;
    tradingWalletAddress: string;
    sourceWallet: string;
    sourceTransactionSignature: string;
    sourceTransactionData: any;
    transactionType: 'buy' | 'sell';
    tokenAddress: string;
    tokenSymbol: string;
    tokenName: string;
    amount: number;
    slippagePercent: number;
    copyDelayApplied: number;
    transactionSignature?: string;
    status: 'pending' | 'success' | 'failed';
    errorMessage?: string;
  }) => {
    try {
      await copyTradeService.logCopyTrade(copyTradeData);
    } catch (error) {
      console.error('❌ Error logging copy trade:', error);
    }
  };

  // Check copy trader rules and execute trades
  const checkCopyTraderRules = (walletAddress: string, transactionData: any) => {
    console.log(`🔍 COPY TRADER RULE CHECK STARTED`);
    console.log(`📊 Wallet: ${walletAddress}`);
    console.log(`📊 Transaction data:`, transactionData);
    console.log(`📊 Transaction type: ${transactionData.type}`);
    console.log(`📊 Is SELL transaction: ${transactionData.type === 'sell'}`);
    console.log(`📊 isCopyingActive: ${isCopyingActiveRef.current}`);
    console.log(`📊 Total rules: ${copyTraderRulesRef.current.length}`);
    console.log(`📊 CURRENT SELECTED WALLETS AT START:`, Array.from(selectedWallets));
    console.log(`📊 TRADING WALLETS COUNT:`, tradingWallets.length);

    if (!isCopyingActiveRef.current) {
      console.log(`❌ Copy trading is not active`);
      return;
    }

    if (copyTraderRulesRef.current.length === 0) {
      console.log(`❌ No copy trader rules configured`);
      return;
    }

    const activeRules = copyTraderRulesRef.current.filter(rule => 
      rule.isActive && 
      rule.sourceWalletAddress.toLowerCase() === walletAddress.toLowerCase()
    );

    console.log(`📊 Active rules for wallet ${walletAddress}: ${activeRules.length}`);
    activeRules.forEach(rule => {
      console.log(`  - Rule: ${rule.name} (ID: ${rule.id})`);
    });

    if (activeRules.length === 0) {
      console.log(`❌ No active rules match wallet ${walletAddress}`);
      return;
    }

    console.log(`✅ Processing ${activeRules.length} active rule(s) for wallet ${walletAddress}`);

    for (const rule of activeRules) {
      try {
        console.log(`🔍 Processing rule: ${rule.name}`);
        console.log(`📊 Rule settings: copyBuys=${rule.copyBuys}, copySells=${rule.copySells}`);
        console.log(`📊 Transaction type: ${transactionData.type}`);
        
        // Check if we should copy this transaction type
        if (!rule.copyBuys && transactionData.type === 'buy') {
          console.log(`⏭️ Skipping buy transaction for rule ${rule.name} (buys disabled)`);
          continue;
        }

        if (!rule.copySells && transactionData.type === 'sell') {
          console.log(`⏭️ Skipping sell transaction for rule ${rule.name} (sells disabled)`);
          console.log(`❌ SELL TRANSACTION BLOCKED: Rule ${rule.name} has copySells=${rule.copySells}`);
          continue;
        }
        
        if (transactionData.type === 'sell') {
          console.log(`✅ SELL TRANSACTION ALLOWED: Rule ${rule.name} has copySells=${rule.copySells}`);
        }

        // Extract token address - try multiple possible locations
        let tokenAddress = '';
        let tokenSymbol = 'Unknown';
        let tokenName = 'Unknown';

        console.log(`🔍 Extracting token info from transaction data...`);

        // Method 1: For BUY transactions, get token from 'to' object
        if (transactionData.type === 'buy' && transactionData.to && transactionData.to.address) {
          tokenAddress = transactionData.to.address;
          tokenSymbol = transactionData.to.token?.symbol || 'Unknown';
          tokenName = transactionData.to.token?.name || tokenSymbol;
          console.log(`📍 Method 1 (buy - to object):`, { tokenAddress, tokenSymbol, tokenName });
        }
        // Method 2: For SELL transactions, get token from 'from' object
        else if (transactionData.type === 'sell' && transactionData.from && transactionData.from.address) {
          tokenAddress = transactionData.from.address;
          tokenSymbol = transactionData.from.token?.symbol || 'Unknown';
          tokenName = transactionData.from.token?.name || tokenSymbol;
          console.log(`📍 Method 2 (sell - from object):`, { tokenAddress, tokenSymbol, tokenName });
        }
        // Method 3: Check token.to (for buy transactions) - LEGACY METHOD
        else if (transactionData.type === 'buy' && transactionData.token?.to) {
          tokenAddress = transactionData.token.to.address || transactionData.token.to.mint || '';
          tokenSymbol = transactionData.token.to.symbol || 'Unknown';
          tokenName = transactionData.token.to.name || tokenSymbol;
          console.log(`📍 Method 3 (legacy buy - token.to):`, { tokenAddress, tokenSymbol, tokenName });
        } 
        // Method 4: Check token.from (for sell transactions) - LEGACY METHOD
        else if (transactionData.type === 'sell' && transactionData.token?.from) {
          tokenAddress = transactionData.token.from.address || transactionData.token.from.mint || '';
          tokenSymbol = transactionData.token.from.symbol || 'Unknown';
          tokenName = transactionData.token.from.name || tokenSymbol;
          console.log(`📍 Method 4 (legacy sell - token.from):`, { tokenAddress, tokenSymbol, tokenName });
        } 
        // Method 5: Direct token object
        else if (transactionData.token) {
          tokenAddress = transactionData.token.address || transactionData.token.mint || '';
          tokenSymbol = transactionData.token.symbol || 'Unknown';
          tokenName = transactionData.token.name || tokenSymbol;
          console.log(`📍 Method 5 (direct token):`, { tokenAddress, tokenSymbol, tokenName });
        }
        // Method 6: Root level token info
        else if (transactionData.tokenAddress || transactionData.mint || transactionData.token_address) {
          tokenAddress = transactionData.tokenAddress || transactionData.mint || transactionData.token_address || '';
          tokenSymbol = transactionData.tokenSymbol || transactionData.symbol || 'Unknown';
          tokenName = transactionData.tokenName || transactionData.name || tokenSymbol;
          console.log(`📍 Method 6 (root level):`, { tokenAddress, tokenSymbol, tokenName });
        }
        // Method 7: Check tokens array (if multiple tokens)
        else if (transactionData.tokens && Array.isArray(transactionData.tokens) && transactionData.tokens.length > 0) {
          const firstToken = transactionData.tokens[0];
          tokenAddress = firstToken.address || firstToken.mint || '';
          tokenSymbol = firstToken.symbol || 'Unknown';
          tokenName = firstToken.name || tokenSymbol;
          console.log(`📍 Method 7 (tokens array):`, { tokenAddress, tokenSymbol, tokenName });
        }

        console.log(`🪙 Token Info for rule ${rule.name}:`, {
          address: tokenAddress,
          symbol: tokenSymbol,
          name: tokenName,
          transactionType: transactionData.type
        });

        if (!tokenAddress || tokenAddress.trim() === '') {
          console.log(`⚠️ No token address found in transaction data for rule ${rule.name}`);
          console.log(`📊 Full transaction data:`, transactionData);
          console.log(`📊 Transaction data structure:`, {
            hasToken: !!transactionData.token,
            tokenKeys: transactionData.token ? Object.keys(transactionData.token) : 'no token object',
            hasTo: !!transactionData.token?.to,
            hasFrom: !!transactionData.token?.from,
            toKeys: transactionData.token?.to ? Object.keys(transactionData.token.to) : 'no to object',
            fromKeys: transactionData.token?.from ? Object.keys(transactionData.token.from) : 'no from object',
            allKeys: Object.keys(transactionData)
          });
          
          // Try alternative token extraction methods
          console.log(`🔍 Trying alternative token extraction methods:`);
          
          // Check if token info is at root level
          if (transactionData.tokenAddress || transactionData.mint || transactionData.token_address) {
            console.log(`📍 Found token at root level:`, {
              tokenAddress: transactionData.tokenAddress,
              mint: transactionData.mint,
              token_address: transactionData.token_address
            });
          }
          
          // Check if there's a tokens array
          if (transactionData.tokens && Array.isArray(transactionData.tokens)) {
            console.log(`📍 Found tokens array:`, transactionData.tokens);
          }
          
          continue;
        }

        // Validate that tokenAddress is not the SOL mint (to prevent circular arbitrage)
        const SOL_MINT = 'So11111111111111111111111111111111111111112';
        if (tokenAddress === SOL_MINT) {
          console.log(`⚠️ Token address is SOL mint, skipping to prevent circular arbitrage for rule ${rule.name}`);
          continue;
        }
        
        if (rule.tokenBlacklist && rule.tokenBlacklist.length > 0) {
          if (rule.tokenBlacklist.includes(tokenAddress)) {
            console.log(`⏭️ Skipping blacklisted token ${tokenAddress} for rule ${rule.name}`);
            continue;
          }
        }

        if (rule.tokenWhitelist && rule.tokenWhitelist.length > 0) {
          if (!rule.tokenWhitelist.includes(tokenAddress)) {
            console.log(`⏭️ Skipping non-whitelisted token ${tokenAddress} for rule ${rule.name}`);
            continue;
          }
        }

        // Check if we have trading wallets configured
        if (!rule.tradingWalletAddresses || rule.tradingWalletAddresses.length === 0) {
          console.log(`⚠️ No trading wallets configured for rule ${rule.name}`);
          continue;
        }

        // Check if the rule's configured wallets are available (like Sniper Bot)
        // Don't depend on selectedWallets - use the rule's configured wallets directly
        const configuredWallets = rule.tradingWalletAddresses || [];
        
        if (configuredWallets.length === 0) {
          console.log(`⚠️ No trading wallets configured for rule ${rule.name}`);
          continue;
        }

        // Verify that the configured wallets actually exist in our trading wallets list
        const validWallets = configuredWallets.filter(addr => 
          tradingWallets.some(w => w.public_key === addr)
        );

        if (validWallets.length === 0) {
          console.log(`⚠️ None of the configured trading wallets are available for rule ${rule.name}`);
          console.log(`📊 Rule configured wallets:`, configuredWallets);
          console.log(`📊 Available trading wallets:`, tradingWallets.map(w => w.public_key));
          continue;
        }

        console.log(`✅ Rule "${rule.name}" has ${validWallets.length} valid trading wallet(s) available`);
        console.log(`📊 Valid wallets:`, validWallets.map(addr => addr.slice(0, 8) + '...'));

        console.log(`✅ Rule "${rule.name}" matched! Executing copy trade...`);
        
        // Calculate the actual slippage that will be used
        let actualSlippagePercent = rule.slippagePercent || parseFloat(slippagePercent);
        
        // Safety check: Ensure minimum slippage for copy trades (Jupiter often needs higher slippage)
        const minimumSlippage = 15; // 15% minimum for copy trades
        if (actualSlippagePercent < minimumSlippage) {
          console.log(`⚠️ Slippage ${actualSlippagePercent}% is too low for copy trading. Increasing to ${minimumSlippage}%`);
          actualSlippagePercent = minimumSlippage;
        }
        
        // For sell transactions, use even higher slippage as they're more volatile
        if (transactionData.type === 'sell' && actualSlippagePercent < 25) {
          console.log(`⚠️ Sell transaction detected. Increasing slippage from ${actualSlippagePercent}% to 25% for better execution`);
          actualSlippagePercent = 25;
        }
        
        console.log(`🎯 Trade Details:`, {
          tokenAddress,
          tokenSymbol,
          tokenName,
          tradeType: transactionData.type,
          tradeAmount: transactionData.type === 'sell' ? 'SELL ALL TOKENS' : `${rule.tradeSizeValue} ${rule.tradeSizeType}`,
          sourceVolume: transactionData.volume?.sol || transactionData.solVolume || 0,
          validWallets: validWallets.length,
          tradeSizeType: rule.tradeSizeType,
          tradeSizeValue: rule.tradeSizeValue,
          // SLIPPAGE DEBUG INFO
          ruleSlippagePercent: rule.slippagePercent,
          globalSlippagePercent: parseFloat(slippagePercent),
          originalSlippagePercent: rule.slippagePercent || parseFloat(slippagePercent),
          finalSlippagePercent: actualSlippagePercent,
          slippageSource: rule.slippagePercent ? 'rule' : 'global',
          slippageAdjusted: actualSlippagePercent !== (rule.slippagePercent || parseFloat(slippagePercent))
        });

        // Execute the copy trade with extracted token info
        const enhancedTransactionData = {
          ...transactionData,
          tokenAddress,
          tokenSymbol,
          tokenName
        };

                  executeCopyTrade(
            rule.id,
            walletAddress,
            enhancedTransactionData,
            {
              solAmountPerTrade: rule.solAmountPerTrade || parseFloat(solAmountPerTrade),
              slippagePercent: actualSlippagePercent,
              maxTradesToCopy: rule.maxTradesToCopy || parseInt(maxTradesToCopy),
              tradingWalletAddresses: validWallets,
              tradeSizeType: rule.tradeSizeType || tradeSizeType,
              tradeSizeValue: rule.tradeSizeValue || tradeSizeValue,
              copyDelaySeconds: rule.copyDelaySeconds || parseFloat(copyDelaySeconds)
            },
            rule.name
          );

      } catch (error) {
        console.error(`❌ Error processing rule ${rule.name}:`, error);
      }
    }
  };

  // Load trading wallets
  const loadTradingWallets = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) {
      setIsLoadingWallets(false);
      return;
    }

    setIsLoadingWallets(true);
    try {
      const wallets = await getUserTradingWallets(effectiveWalletAddress);
      setTradingWallets(wallets || []);
      
      // FIXED: Load balances for the fetched wallets directly (don't wait for state update)
      await refreshTradingWalletBalances(wallets || []);
    } catch (error) {
      console.error('Error loading trading wallets:', error);
      showNotification('Failed to load trading wallets', 'error');
    } finally {
      setIsLoadingWallets(false);
    }
  };

  // Refresh trading wallet balances
  const refreshTradingWalletBalances = async (walletsToRefresh?: TradingWallet[]) => {
    const wallets = walletsToRefresh || tradingWallets;
    if (wallets.length === 0) return;

    try {
      const balancePromises = wallets.map(async (wallet) => {
        try {
          const balanceInLamports = await getBalance(wallet.public_key);
          return { 
            address: wallet.public_key, 
            balance: balanceInLamports ? balanceInLamports / 1_000_000_000 : 0 // Convert to SOL
          };
        } catch (error) {
          console.error(`Error getting balance for ${wallet.public_key}:`, error);
          return { address: wallet.public_key, balance: 0 };
        }
      });

      const balances = await Promise.all(balancePromises);
      
      const balanceMap = new Map<string, number>();
      balances.forEach(({ address, balance }) => {
        balanceMap.set(address, balance);
      });
      
      console.log(`💰 Copy Trader: Loaded balances for ${balances.length} wallets:`, 
        balances.map(b => `${b.address.slice(0, 8)}... = ${b.balance.toFixed(3)} SOL`));
      
      setWalletBalances(balanceMap);
    } catch (error) {
      console.error('Error refreshing wallet balances:', error);
    }
  };

  // Load copy trader rules
  const loadCopyTraderRules = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) {
      setIsLoadingRules(false);
      return;
    }

    setIsLoadingRules(true);
    try {
      console.log(`📋 Loading copy trader rules for wallet: ${effectiveWalletAddress}`);
      
      const rules = await copyTradeService.getCopyTradeRules(effectiveWalletAddress);

      console.log(`📊 Raw rules from database:`, { rules, count: rules?.length || 0 });

      const formattedRules: LocalCopyTraderRule[] = (rules || []).map(rule => {
        console.log(`📋 Formatting rule ${rule.name}:`, {
          copy_buys: rule.copy_buys,
          copy_sells: rule.copy_sells,
          copyBuys: rule.copy_buys,
          copySells: rule.copy_sells
        });
        
        return {
          ...rule, // Spread all database fields
          isActive: false, // Always load bots as inactive when widget opens
          // Legacy property mappings for backward compatibility
          sourceWalletAddress: rule.source_wallet_address,
          sourceWalletName: rule.source_wallet_name,
          tradingWalletAddresses: rule.trading_wallet_addresses,
          copyBuys: rule.copy_buys,
          copySells: rule.copy_sells,
          tokenWhitelist: rule.token_whitelist,
          tokenBlacklist: rule.token_blacklist,
          solAmountPerTrade: parseFloat(rule.trade_size_value),
          maxTradesToCopy: rule.max_trades_to_copy,
          slippagePercent: parseFloat(rule.max_slippage_percent),
          copyDelaySeconds: rule.copy_delay_seconds,
          tradeSizeType: rule.trade_size_type,
          tradeSizeValue: rule.trade_size_value
        };
      });

      console.log(`📋 Formatted rules:`, formattedRules);
      
      // Deactivate all bots when widget loads
      if (formattedRules.length > 0) {
        console.log(`🛑 Deactivating all ${formattedRules.length} copy trader bots on widget load...`);
        
        const deactivationPromises = formattedRules.map(async (rule) => {
          try {
            await copyTradeService.toggleCopyTradeRule(effectiveWalletAddress, rule.id, false);
            console.log(`✅ Deactivated rule on load: ${rule.name}`);
          } catch (error) {
            console.error(`Error deactivating rule ${rule.name} on load:`, error);
          }
        });

        await Promise.all(deactivationPromises);
        console.log(`✅ All copy trader bots deactivated on widget load`);
      }
      
      setCopyTraderRules(formattedRules);
      copyTraderRulesRef.current = formattedRules;
      
      // Clear any selected wallets since all bots are inactive on load
      setSelectedWalletsWithDebug(new Set<string>());
      console.log(`🔄 Cleared selected wallets since all bots are inactive on widget load`);
      
      console.log(`✅ Copy trader rules loaded: ${formattedRules.length} rules`);

    } catch (error) {
      console.error('Error loading copy trader rules:', error);
      showNotification('Failed to load copy trader rules', 'error');
    } finally {
      setIsLoadingRules(false);
    }
  };

  // Handle rule toggle
  const handleRuleToggle = async (ruleId: string) => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) {
      showNotification('Wallet not connected', 'error');
      return;
    }

    try {
      const rule = copyTraderRules.find(r => r.id === ruleId);
      if (!rule) return;

      const newActiveState = !rule.isActive;

      // Update in database using the new service
      await copyTradeService.toggleCopyTradeRule(effectiveWalletAddress, ruleId, newActiveState);

      // Update local state
      const updatedRules = copyTraderRules.map(r => 
        r.id === ruleId ? { ...r, isActive: newActiveState } : r
      );
      setCopyTraderRules(updatedRules);
      copyTraderRulesRef.current = updatedRules;

      // Auto-select trading wallets for active rules
      if (newActiveState) {
        // When activating a rule, auto-select its trading wallets
        const walletsToAdd = new Set<string>(selectedWallets); // Keep existing selections
        let walletsAdded = 0;
        
        rule.tradingWalletAddresses?.forEach(addr => {
          if (tradingWallets.some(w => w.public_key === addr)) {
            walletsToAdd.add(addr);
            walletsAdded++;
            console.log(`✅ Auto-selected wallet for activated rule: ${addr.slice(0, 8)}...`);
          }
        });
        
        if (walletsAdded > 0) {
          setSelectedWalletsWithDebug(walletsToAdd);
          console.log(`🔧 Auto-selected ${walletsAdded} wallets for rule ${rule.name}`);
        }
      } else {
        // When deactivating a rule, update selected wallets based on remaining active rules
        const activeRules = updatedRules.filter(r => r.isActive);
        const walletsToSelect = new Set<string>();
        activeRules.forEach(activeRule => {
          activeRule.tradingWalletAddresses?.forEach(addr => {
            if (tradingWallets.some(w => w.public_key === addr)) {
              walletsToSelect.add(addr);
            }
          });
        });
        setSelectedWalletsWithDebug(walletsToSelect);
        console.log(`🔄 Updated selected wallets based on remaining active rules:`, Array.from(walletsToSelect));
      }

      // Handle websocket subscriptions
      if (newActiveState) {
        // Subscribe to the source wallet
        subscribeToWalletRoom(rule.sourceWalletAddress);
        showNotification(`Activated copy trader rule: ${rule.name}`, 'success');
      } else {
        // Check if any other rules are using this wallet, if not, unsubscribe
        const otherActiveRules = updatedRules.filter(r => 
          r.id !== ruleId && 
          r.isActive && 
          r.sourceWalletAddress === rule.sourceWalletAddress
        );
        
        if (otherActiveRules.length === 0) {
          unsubscribeFromWalletRoom(rule.sourceWalletAddress);
        }
        
        showNotification(`Deactivated copy trader rule: ${rule.name}`, 'success');
      }

    } catch (error) {
      console.error('Error toggling rule:', error);
      showNotification('Failed to toggle rule', 'error');
    }
  };

  // Handle rule deletion
  const handleRuleDelete = async (ruleId: string) => {
    const rule = copyTraderRules.find(r => r.id === ruleId);
    if (!rule) return;

    setRuleToDelete({
      id: ruleId,
      name: rule.name,
      isActive: rule.isActive
    });
    setShowDeleteConfirm(true);
  };

  const confirmRuleDelete = async () => {
    if (!ruleToDelete) return;

    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) {
      showNotification('Wallet not connected', 'error');
      return;
    }

    try {
      // Delete from database using the new service
      await copyTradeService.deleteCopyTradeRule(effectiveWalletAddress, ruleToDelete.id);

      // Find the rule to get the source wallet
      const rule = copyTraderRules.find(r => r.id === ruleToDelete.id);
      
      // Update local state
      const updatedRules = copyTraderRules.filter(r => r.id !== ruleToDelete.id);
      setCopyTraderRules(updatedRules);
      copyTraderRulesRef.current = updatedRules;

      // Unsubscribe from wallet if no other rules are using it
      if (rule) {
        const otherRulesUsingWallet = updatedRules.filter(r => 
          r.sourceWalletAddress === rule.sourceWalletAddress && r.isActive
        );
        
        if (otherRulesUsingWallet.length === 0) {
          unsubscribeFromWalletRoom(rule.sourceWalletAddress);
        }
      }

      showNotification(`Deleted copy trader rule: ${ruleToDelete.name}`, 'success');

    } catch (error) {
      console.error('Error deleting rule:', error);
      showNotification('Failed to delete rule', 'error');
    } finally {
      setShowDeleteConfirm(false);
      setRuleToDelete(null);
    }
  };

  const cancelRuleDelete = () => {
    setShowDeleteConfirm(false);
    setRuleToDelete(null);
  };

  // Load trade history
  const loadTradeHistory = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) return;

    setIsLoadingTradeHistory(true);
    try {
      const trades = await copyTradeService.getCopyTradeHistory(effectiveWalletAddress, 50);
      setTradeHistory(trades || []);
    } catch (error) {
      console.error('Error loading trade history:', error);
    } finally {
      setIsLoadingTradeHistory(false);
    }
  };

  // Subscribe to wallet room
  const subscribeToWalletRoom = (walletAddress: string) => {
    if (subscribedWalletsRef.current.has(walletAddress)) {
      console.log(`Already subscribed to wallet: ${walletAddress}`);
      return;
    }

    console.log(`🔔 Subscribing to wallet transactions: ${walletAddress}`);
    
    // Join the wallet room
    wsService.joinRoom(`wallet:${walletAddress}`);
    
    // Set up event listener
    const unsubscribe = wsService.on(`wallet:${walletAddress}`, (data) => {
      console.log(`📨 New transaction for wallet ${walletAddress}:`, data);
      checkCopyTraderRules(walletAddress, data);
    });

    // Store the unsubscribe function
    const currentSubscriptions = walletSubscriptionsRef.current.get(walletAddress) || [];
    currentSubscriptions.push(unsubscribe);
    walletSubscriptionsRef.current.set(walletAddress, currentSubscriptions);
    
    // Add to subscribed set
    subscribedWalletsRef.current.add(walletAddress);
  };

  // Unsubscribe from wallet room
  const unsubscribeFromWalletRoom = (walletAddress: string) => {
    if (!subscribedWalletsRef.current.has(walletAddress)) {
      return;
    }

    console.log(`🔕 Unsubscribing from wallet transactions: ${walletAddress}`);

    // Leave the room
    wsService.leaveRoom(`wallet:${walletAddress}`);

    // Clean up event listeners
    const subscriptions = walletSubscriptionsRef.current.get(walletAddress) || [];
    subscriptions.forEach(unsubscribe => unsubscribe());
    walletSubscriptionsRef.current.delete(walletAddress);

    // Remove from subscribed set
    subscribedWalletsRef.current.delete(walletAddress);
  };

  function showCopyTraderNotification(
    type: 'loading' | 'success' | 'error',
    botName: string,
    tokenSymbol: string,
    tokenName: string,
    amount: number,
    walletAddress: string,
    tradeType: 'buy' | 'sell',
    txSignature?: string,
    errorMessage?: string,
    sourceWallet?: string
  ) {
    const shortWallet = `${walletAddress.slice(0, 4)}...${walletAddress.slice(-4)}`;
    const shortSourceWallet = sourceWallet ? `${sourceWallet.slice(0, 4)}...${sourceWallet.slice(-4)}` : '';
    
    let message = '';
    if (type === 'loading') {
      if (tradeType === 'sell') {
        message = `Copying sell of ALL ${tokenSymbol} tokens from ${shortSourceWallet}...`;
      } else {
        message = `Copying ${tradeType} of ${amount} SOL worth of ${tokenSymbol} from ${shortSourceWallet}...`;
      }
    } else if (type === 'success') {
      if (tradeType === 'sell') {
        message = `✅ Successfully sold ALL ${tokenSymbol} tokens from ${shortSourceWallet}`;
      } else {
        message = `✅ Successfully copied ${tradeType} of ${tokenSymbol} (${amount} SOL) from ${shortSourceWallet}`;
      }
    } else {
      message = `❌ Failed to copy ${tradeType} of ${tokenSymbol}: ${errorMessage}`;
    }

    const notificationId = showNotification(message, type);
    
    // Auto-hide success notifications after 3 seconds
    if (type === 'success' && notificationId) {
      setTimeout(() => {
        hideNotification(notificationId, 'success');
      }, 3000);
    }
    
    return notificationId;
  }

  // Initialize connection and setup
  useEffect(() => {
    const initializeConnection = async () => {
      const effectiveWalletAddress = getEffectiveWalletAddress();
      if (!effectiveWalletAddress) {
        setConnectionStatus('disconnected');
        return;
      }

      setConnectionStatus('connecting');
      setIsLoading(true);

      try {
        // Ensure copy_trades table exists before doing anything else
        await ensureCopyTradesTable();
        
        // Load trading wallets first, then rules (to avoid race conditions)
        await loadTradingWallets();
        await loadCopyTraderRules();
        
        // Load rule statistics
        const stats = await copyTradeService.getRuleStatistics(effectiveWalletAddress);
        setRuleStatistics(stats);

        // Connect to websocket
        const connected = await wsService.connect();
        if (connected) {
          setConnectionStatus('connected');
          
          // Don't subscribe to any wallets on load since all bots are inactive
          console.log(`🔕 No wallet subscriptions on load - all bots are inactive`);
        } else {
          setConnectionStatus('error');
        }
      } catch (error) {
        console.error('Error initializing copy trader:', error);
        setConnectionStatus('error');
      } finally {
        setIsLoading(false);
      }
    };

    const checkConnectionStatus = () => {
      if (wsService.isConnected()) {
        setConnectionStatus('connected');
      } else {
        setConnectionStatus('disconnected');
      }
    };

    initializeConnection();

    // Set up periodic connection check
    const connectionCheckInterval = setInterval(checkConnectionStatus, 5000);

    return () => {
      clearInterval(connectionCheckInterval);
      
      // Clean up all subscriptions
      for (const [walletAddress, subscriptions] of walletSubscriptionsRef.current.entries()) {
        subscriptions.forEach(unsubscribe => unsubscribe());
        wsService.leaveRoom(`wallet:${walletAddress}`);
      }
      walletSubscriptionsRef.current.clear();
      subscribedWalletsRef.current.clear();
      
      // Reset copy trading state on unmount
      isCopyingActiveRef.current = false;
      
      // Clear all tracking refs
      ruleBuyCountRef.current.clear();
      ruleSellCountRef.current.clear();
      recentlyCopiedTransactionsRef.current.clear();
      walletRotationRef.current.clear();
      ruleExecutionLockRef.current.clear();
    };
  }, [walletUser.walletAddress]);

  // Update refs when rules change
  useEffect(() => {
    copyTraderRulesRef.current = copyTraderRules;
  }, [copyTraderRules]);

  // Debug function to test copy trader with fake transaction data
  const testCopyTraderWithFakeData = (walletAddress?: string, transactionType: 'buy' | 'sell' = 'buy') => {
    console.log(`🧪 TESTING COPY TRADER WITH FAKE ${transactionType.toUpperCase()} DATA`);
    
    const testWallet = walletAddress || (copyTraderRules.length > 0 ? copyTraderRules[0].sourceWalletAddress : '');
    if (!testWallet) {
      console.log(`❌ No wallet address provided and no rules configured`);
      return;
    }

    const fakeTransactionData = transactionType === 'buy' ? {
      tx: 'fake_tx_signature_' + Date.now(),
      amount: 1.5,
      priceUsd: 0.001,
      solVolume: 1.5,
      volume: 1500,
      type: 'buy',
      wallet: testWallet,
      time: Date.now(),
      program: 'pump.fun',
      token: {
        to: {
          name: 'Test Token',
          symbol: 'TEST',
          image: 'https://example.com/test.png',
          decimals: 6,
          amount: 1500000,
          address: 'TestTokenMintAddress123456789'
        }
      }
    } : {
      tx: 'fake_sell_tx_signature_' + Date.now(),
      amount: 0.8,
      priceUsd: 0.001,
      solVolume: 0.8,
      volume: 800,
      type: 'sell',
      wallet: testWallet,
      time: Date.now(),
      program: 'pump.fun',
      token: {
        from: {
          name: 'Test Token',
          symbol: 'TEST',
          image: 'https://example.com/test.png',
          decimals: 6,
          amount: 800000,
          address: 'TestTokenMintAddress123456789'
        }
      }
    };

    console.log(`🧪 Testing with wallet: ${testWallet}`);
    console.log(`🧪 Fake ${transactionType} transaction data:`, fakeTransactionData);
    
    checkCopyTraderRules(testWallet, fakeTransactionData);
  };

  // Expose debug functions to window for manual testing
  useEffect(() => {
    (window as any).testCopyTrader = testCopyTraderWithFakeData;
    (window as any).testCopyTraderSell = (walletAddress?: string) => testCopyTraderWithFakeData(walletAddress, 'sell');
    (window as any).debugCopyTrader = () => {
      console.log(`🔍 COPY TRADER DEBUG INFO:`, {
        isCopyingActive: isCopyingActiveRef.current,
        totalRules: copyTraderRulesRef.current.length,
        activeRules: copyTraderRulesRef.current.filter(r => r.isActive).length,
        subscribedWallets: Array.from(subscribedWalletsRef.current),
        connectionStatus,
        selectedWallets: Array.from(selectedWallets),
        tradingWallets: tradingWallets.length,
        rules: copyTraderRulesRef.current.map(r => ({
          id: r.id,
          name: r.name,
          isActive: r.isActive,
          sourceWallet: r.sourceWalletAddress,
          tradingWallets: r.tradingWalletAddresses
        }))
      });
    };
    
    (window as any).fixCopyTraderWallets = () => {
      console.log(`🔧 MANUALLY FIXING COPY TRADER WALLET SELECTION...`);
      const activeRules = copyTraderRules.filter(rule => rule.isActive);
      if (activeRules.length > 0) {
        const walletsToSelect = new Set<string>();
        activeRules.forEach(rule => {
          rule.tradingWalletAddresses?.forEach(addr => {
            if (tradingWallets.some(w => w.public_key === addr)) {
              walletsToSelect.add(addr);
              console.log(`🔧 Manually selecting wallet: ${addr}`);
            }
          });
        });
        
        console.log(`🔧 Force selecting wallets:`, Array.from(walletsToSelect));
        setSelectedWalletsWithDebug(walletsToSelect);
        return Array.from(walletsToSelect);
      }
      return [];
    };

    (window as any).refreshCopyTraderBalances = () => {
      console.log(`💰 MANUALLY REFRESHING COPY TRADER WALLET BALANCES...`);
      refreshTradingWalletBalances();
      return 'Refreshing balances...';
    };

    return () => {
      delete (window as any).testCopyTrader;
      delete (window as any).testCopyTraderSell;
      delete (window as any).debugCopyTrader;
      delete (window as any).fixCopyTraderWallets;
      delete (window as any).refreshCopyTraderBalances;
    };
  }, [copyTraderRules, selectedWallets, connectionStatus, tradingWallets]);

  // Update copying active ref and set copying active based on active rules
  useEffect(() => {
    const hasActiveRules = copyTraderRules.some(rule => rule.isActive);
    setIsCopyingActive(hasActiveRules);
    isCopyingActiveRef.current = hasActiveRules;
    
    console.log(`🎯 Copy Trader Status Update:`, {
      hasActiveRules,
      activeRulesCount: copyTraderRules.filter(r => r.isActive).length,
      totalRules: copyTraderRules.length,
      isCopyingActive: hasActiveRules
    });
  }, [copyTraderRules]);

  // Ensure wallet selection is maintained when trading wallets or rules change
  useEffect(() => {
    if (tradingWallets.length > 0 && copyTraderRules.length > 0) {
      const activeRules = copyTraderRules.filter(rule => rule.isActive);
      if (activeRules.length > 0) {
        const walletsToSelect = new Set<string>();
        activeRules.forEach(rule => {
          rule.tradingWalletAddresses?.forEach(addr => {
            // Only select wallets that actually exist in our trading wallets list
            if (tradingWallets.some(w => w.public_key === addr)) {
              walletsToSelect.add(addr);
            }
          });
        });
        
        // Only update if the selection has actually changed
        const currentSelection = Array.from(selectedWallets).sort();
        const newSelection = Array.from(walletsToSelect).sort();
        
        if (JSON.stringify(currentSelection) !== JSON.stringify(newSelection)) {
          console.log(`🔄 Ensuring wallet selection is correct:`, {
            current: currentSelection,
            new: newSelection,
            activeRules: activeRules.length,
            tradingWallets: tradingWallets.length
          });
          setSelectedWalletsWithDebug(walletsToSelect);
        }
      }
    }
  }, [tradingWallets, copyTraderRules, selectedWallets]);

  // Calculate total SOL needed for copying
  const calculateTotalSolNeeded = () => {
    const activeRulesCount = copyTraderRules.filter(rule => rule.isActive).length;
    const avgTradesPerRule = parseInt(maxTradesToCopy) / 2; // Estimate
    const avgAmountPerTrade = parseFloat(solAmountPerTrade);
    return activeRulesCount * avgTradesPerRule * avgAmountPerTrade;
  };

  // Get total available balance
  const getTotalAvailableBalance = () => {
    let total = 0;
    selectedWallets.forEach(walletAddress => {
      total += walletBalances.get(walletAddress) || 0;
    });
    return total;
  };

  // Toggle wallet selection
  const toggleWalletSelection = (walletAddress: string) => {
    const newSelected = new Set(selectedWallets);
    if (newSelected.has(walletAddress)) {
      newSelected.delete(walletAddress);
    } else {
      newSelected.add(walletAddress);
    }
    setSelectedWalletsWithDebug(newSelected);
  };

  // Render wallet selection section (exactly like SniperBot)
  const renderWalletSelection = () => (
    <div className="mb-1.5">
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-white text-xs font-medium">Trading Wallets</h4>
        <button
          onClick={() => refreshTradingWalletBalances()}
          className="p-1 text-gray-400 hover:text-[#24c35b] transition-colors"
          title="Refresh wallet balances"
        >
          <RefreshCw size={12} />
        </button>
      </div>
      {isLoadingWallets ? (
        <div className="flex items-center justify-center py-2">
          <Loader size={12} className="text-[#24c35b] animate-spin mr-1" />
          <span className="text-gray-400 text-xs">Loading...</span>
        </div>
      ) : tradingWallets.length === 0 ? (
        <div className="text-center py-2">
          <p className="text-gray-400 text-xs">No wallets found</p>
        </div>
      ) : (
        <div className="space-y-1 max-h-28 overflow-y-auto">
          {tradingWallets.map((wallet) => {
            const isSelected = selectedWallets.has(wallet.public_key);
            const balance = walletBalances.get(wallet.public_key) || 0;
            
            return (
              <div
                key={wallet.public_key}
                className={`p-1 rounded border cursor-pointer transition-all ${
                  isSelected 
                    ? 'border-[#24c35b] bg-[#24c35b]/10' 
                    : 'border-gray-200 bg-white hover:border-[#24c35b]/40'
                }`}
                onClick={() => toggleWalletSelection(wallet.public_key)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded border mr-1.5 flex items-center justify-center ${
                      isSelected ? 'border-[#24c35b] bg-[#24c35b]' : 'border-gray-400'
                    }`}>
                      {isSelected && (
                        <div className="w-1.5 h-1.5 bg-white rounded-sm"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-1">
                        <p className="text-black text-xs font-medium">{wallet.name}</p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyWalletAddress(wallet.public_key);
                          }}
                          className="p-0.5 text-gray-600 hover:text-[#24c35b] transition-colors"
                          title="Copy wallet address"
                        >
                          {copiedWallets.has(wallet.public_key) ? (
                            <Check size={10} className="text-[#24c35b]" />
                          ) : (
                            <Copy size={10} />
                          )}
                        </button>
                      </div>
                      <p className="text-gray-600 text-xs font-mono">
                        {wallet.public_key.slice(0, 6)}...{wallet.public_key.slice(-4)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-mono text-black">
                      {balance.toFixed(3)} SOL
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Render source wallet input step (styled like SniperBot rules step)
  const renderSourceWalletInput = () => (
    <div className="space-y-1.5">
      <div className="text-center mb-1.5">
        <h3 className="text-white text-base font-semibold mb-0.5">
          {editingBotId ? 'Edit Copy Trader' : 'Create New Copy Trader'}
        </h3>
        <p className="text-gray-500 text-xs">
          {editingBotId ? 'Modify your copy trader settings' : 'Set up automated copy trading'}
        </p>
      </div>

      {/* Bot Name */}
      <div>
        <label className="text-white text-xs font-medium mb-1 block">
          Copy Trader Name
        </label>
        <input
          type="text"
          value={newBotName}
          onChange={(e) => setNewBotName(e.target.value)}
          className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
          placeholder="e.g., Low Cap Hunter"
        />
      </div>

      {/* Source Wallet Address */}
      <div>
        <label className="text-black text-xs font-medium mb-1 block">
          Source Wallet Address
        </label>
        <input
          type="text"
          value={sourceWalletAddress}
          onChange={(e) => setSourceWalletAddress(e.target.value)}
          className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
          placeholder="Enter wallet address to copy trade..."
        />
        <p className="text-gray-500 text-xs mt-1">
          This is the wallet whose trades you want to copy
        </p>
      </div>

      {/* Source Wallet Name */}
      <div>
        <label className="text-black text-xs font-medium mb-1 block">
          Source Wallet Name (Optional)
        </label>
        <input
          type="text"
          value={sourceWalletName}
          onChange={(e) => setSourceWalletName(e.target.value)}
          className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
          placeholder="e.g., Alpha Trader, Whale #1"
        />
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-2">
        <button
          onClick={() => {
            setIsCreatingBot(false);
            setEditingBotId(null); // Reset editing state
            setNewBotName('');
            setSourceWalletAddress('');
            setSourceWalletName('');
            setSelectedWalletsWithDebug(new Set()); // Clear selected wallets
          }}
          className="px-3 py-1.5 rounded border border-gray-600/50 text-gray-400 hover:text-white hover:border-gray-500 transition-all text-xs"
        >
          Cancel
        </button>
        <button
          onClick={() => setCreationStep('config')}
          disabled={!newBotName.trim() || !sourceWalletAddress.trim()}
          className="px-3 py-1.5 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all text-xs disabled:opacity-50"
        >
          Next: Configure
        </button>
      </div>
    </div>
  );

  // Render configuration creation step (styled like SniperBot config step)
  const renderConfigCreation = () => (
    <div className="space-y-1.5">
      <div className="text-center mb-1.5">
        <h3 className="text-white text-base font-semibold mb-0.5">
          {editingBotId ? `Update Bot: ${newBotName}` : `Configure Bot: ${newBotName}`}
        </h3>
        <p className="text-gray-500 text-xs">
          {editingBotId ? 'Update wallet and copy settings' : 'Select wallets and copy settings'}
        </p>
      </div>

      {/* Reuse existing wallet selection component */}
      {renderWalletSelection()}
      
      {/* Trading Configuration in Grid */}
      <div className="grid grid-cols-3 gap-2 mb-1.5">
        <div>
          <label className="text-white text-xs font-medium mb-1 block">
            SOL per Trade
          </label>
          <input
            type="text"
            value={tradeSizeValue}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                setTradeSizeValue(value);
              }
            }}
            className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]"
            inputMode="decimal"
          />
        </div>

        <div>
          <label className="text-white text-xs font-medium mb-1 block">
            Max Trades
          </label>
          <input
            type="text"
            value={maxTradesToCopy}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                setMaxTradesToCopy(value);
              }
            }}
            className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]"
            inputMode="numeric"
          />
        </div>

        <div>
          <label className="text-white text-xs font-medium mb-1 block">
            Slippage (%)
          </label>
          <input
            type="text"
            value={slippagePercent}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                setSlippagePercent(value);
              }
            }}
            className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]"
            inputMode="decimal"
          />
        </div>
      </div>

      {/* Trade Size Type Configuration */}
      <div className="mb-1.5">
        <label className="text-white text-xs font-medium mb-1 block">
          Trade Size Type
        </label>
        <div className="grid grid-cols-2 gap-1">
          <button
            onClick={() => setTradeSizeType('fixed')}
            className={`p-1.5 rounded border text-xs font-medium transition-all ${
              tradeSizeType === 'fixed'
                ? 'border-[#24c35b] bg-[#24c35b]/20 text-[#24c35b]'
                : 'border-gray-600/20 bg-gray-600/5 text-gray-400 hover:border-[#24c35b]/40 hover:text-white'
            }`}
          >
            <div className="text-center">
              <div className="font-medium">Fixed</div>
              <div className="text-[10px] mt-0.5 opacity-80">Same amount</div>
            </div>
          </button>

          <button
            onClick={() => setTradeSizeType('percentage')}
            className={`p-1.5 rounded border text-xs font-medium transition-all ${
              tradeSizeType === 'percentage'
                ? 'border-[#24c35b] bg-[#24c35b]/20 text-[#24c35b]'
                : 'border-gray-600/20 bg-gray-600/5 text-gray-400 hover:border-[#24c35b]/40 hover:text-white'
            }`}
          >
            <div className="text-center">
              <div className="font-medium">Percentage</div>
              <div className="text-[10px] mt-0.5 opacity-80">% of source</div>
            </div>
          </button>
        </div>

        {/* Trade size type explanation */}
        <div className="mt-1 p-1 rounded bg-gray-600/5 border border-gray-600/20 text-[10px] text-gray-400">
          {tradeSizeType === 'fixed' ? (
            <>
              <span className="text-green-400 font-medium">Fixed:</span> Always trade exactly {tradeSizeValue} SOL regardless of how much the source wallet trades
            </>
          ) : (
            <>
              <span className="text-blue-400 font-medium">Percentage:</span> Trade {tradeSizeValue}% of whatever the source wallet trades (if they trade 1 SOL, you trade {(parseFloat(tradeSizeValue) / 100).toFixed(3)} SOL)
            </>
          )}
        </div>
      </div>

      {/* Copy Delay and Trade Types */}
      <div className="grid grid-cols-2 gap-2 mb-1.5">
        <div>
          <label className="text-white text-xs font-medium mb-1 block">
            Copy Delay (sec)
          </label>
          <div className="flex flex-wrap gap-1 mb-2">
            {[0.1, 0.5, 1, 2, 5].map(delay => (
              <button
                key={delay}
                onClick={() => setCopyDelaySeconds(delay.toString())}
                className={`p-1.5 rounded border text-xs font-medium transition-all ${
                  parseFloat(copyDelaySeconds) === delay
                    ? 'border-[#24c35b] bg-[#24c35b]/20 text-[#24c35b]'
                    : 'border-gray-600/20 bg-gray-600/5 text-gray-400 hover:border-[#24c35b]/40 hover:text-white'
                }`}
              >
                {delay}s
              </button>
            ))}
          </div>
          <input
            type="text"
            min="0"
            step="0.1"
            value={copyDelaySeconds}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                setCopyDelaySeconds(value);
              }
            }}
            className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]"
            placeholder="Custom delay in seconds"
            inputMode="decimal"
          />
        </div>

        <div>
          <label className="text-white text-xs font-medium mb-1 block">
            Copy Types
          </label>
          <div className="flex gap-1">
            <button
              onClick={() => setCopyBuys(!copyBuys)}
              className={`flex-1 px-2 py-1.5 rounded border text-xs font-medium transition-all ${
                copyBuys
                  ? 'border-[#24c35b] bg-[#24c35b]/20 text-[#24c35b]'
                  : 'border-gray-600/20 bg-gray-600/5 text-gray-400 hover:border-[#24c35b]/40 hover:text-white'
              }`}
            >
              Buys
            </button>
            <button
              onClick={() => setCopySells(!copySells)}
              className={`flex-1 px-2 py-1.5 rounded border text-xs font-medium transition-all ${
                copySells
                  ? 'border-[#24c35b] bg-[#24c35b]/20 text-[#24c35b]'
                  : 'border-gray-600/20 bg-gray-600/5 text-gray-400 hover:border-[#24c35b]/40 hover:text-white'
              }`}
            >
              Sells
            </button>
          </div>
        </div>
      </div>

      {/* Summary - styled like SniperBot */}
      {selectedWallets.size > 0 && (
        <div className="p-1.5 rounded border border-gray-600/20 bg-gray-600/5">
          <h5 className="text-white text-xs font-medium mb-1">Summary</h5>
          
          {/* Basic Configuration */}
          <div className="grid grid-cols-2 gap-1 text-xs mb-2">
            <div className="flex justify-between">
              <span className="text-gray-500">SOL per trade:</span>
              <span className="text-white font-mono">{parseFloat(tradeSizeValue) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Max trades:</span>
              <span className="text-white font-mono">{parseInt(maxTradesToCopy) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Wallets:</span>
              <span className="text-white font-mono">{selectedWallets.size}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Trade type:</span>
              <span className="text-white font-mono">
                {tradeSizeType.charAt(0).toUpperCase() + tradeSizeType.slice(1)}
              </span>
            </div>
          </div>

          {/* Budget Calculations */}
          <div className="grid grid-cols-2 gap-1 text-xs mb-2 pb-1 border-b border-gray-600/30">
            <div className="flex justify-between">
              <span className="text-gray-500">Total needed:</span>
              <span className="text-white font-mono font-bold">{calculateTotalSolNeeded().toFixed(3)} SOL</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Available:</span>
              <span className={`font-mono font-bold ${getTotalAvailableBalance() >= calculateTotalSolNeeded() ? 'text-[#24c35b]' : 'text-red-400'}`}>
                {getTotalAvailableBalance().toFixed(3)} SOL
              </span>
            </div>
          </div>

          {/* Copy settings explanation */}
          <div className="mb-2 p-1 rounded bg-gray-600/5 border border-gray-600/20 text-[10px] text-gray-400">
            <span className="text-[#24c35b] font-medium">Copy Settings:</span> Copy {copyBuys && copySells ? 'both buys and sells' : copyBuys ? 'only buys' : copySells ? 'only sells' : 'no trades'} with {copyDelaySeconds} second delay
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-2">
        <button
          onClick={() => setCreationStep('wallet')}
          className="px-3 py-1.5 rounded border border-gray-600/50 text-gray-400 hover:text-white hover:border-gray-500 transition-all text-xs"
        >
          Back
        </button>
        <button
          onClick={handleFinishSetup}
          disabled={isLoading || selectedWallets.size === 0 || (!copyBuys && !copySells)}
          className="px-4 py-2 rounded-lg bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all text-xs disabled:opacity-50 flex items-center"
        >
          {isLoading ? (
            <>
              <Loader size={16} className="animate-spin mr-1" />
              {editingBotId ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            editingBotId ? 'Update Bot' : 'Create Bot'
          )}
        </button>
      </div>
    </div>
  );

  // Handle edit bot
  const handleEditBot = (rule: LocalCopyTraderRule) => {
    setEditingBotId(rule.id);
    setNewBotName(rule.name);
    setSourceWalletAddress(rule.sourceWalletAddress);
    setSourceWalletName(rule.sourceWalletName || '');
    setTradeSizeType(rule.tradeSizeType || 'fixed');
    setTradeSizeValue(rule.tradeSizeValue || '0.1');
    setMaxTradesToCopy((rule.maxTradesToCopy || 10).toString());
    setSlippagePercent((rule.slippagePercent || 35).toString());
    setCopyDelaySeconds((rule.copy_delay_seconds || 2).toString()); // Use the database field name
    setCopyBuys(rule.copyBuys !== false);
    setCopySells(rule.copySells !== false);
    
    // Select the trading wallets for this rule
    const ruleWallets = new Set(rule.tradingWalletAddresses || []);
    setSelectedWalletsWithDebug(ruleWallets);
    
    setIsCreatingBot(true);
    setCreationStep('wallet');
  };

  // Handle finish setup
  const handleFinishSetup = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) {
      showNotification('Wallet not connected', 'error');
      return;
    }

    if (!newBotName.trim()) {
      showNotification('Please enter a bot name', 'error');
      return;
    }

    if (!sourceWalletAddress.trim()) {
      showNotification('Please enter a source wallet address', 'error');
      return;
    }

    if (selectedWallets.size === 0) {
      showNotification('Please select at least one trading wallet', 'error');
      return;
    }

    if (!copyBuys && !copySells) {
      showNotification('Please select at least one trade type to copy', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const tradingWalletAddresses = Array.from(selectedWallets);
      
      console.log(`🔧 Creating copy trader rule:`, {
        name: newBotName.trim(),
        walletAddress: effectiveWalletAddress,
        sourceWallet: sourceWalletAddress.trim(),
        tradingWallets: tradingWalletAddresses,
        editingBotId
      });

      const ruleData: CreateCopyTradeRuleRequest = {
        name: newBotName.trim(),
        source_wallet_address: sourceWalletAddress.trim(),
        source_wallet_name: sourceWalletName.trim() || undefined,
        trading_wallet_addresses: tradingWalletAddresses,
        copy_delay_seconds: parseFloat(copyDelaySeconds), // Use parseFloat to support decimal values like 0.1
        trade_size_type: tradeSizeType,
        trade_size_value: tradeSizeValue,
        max_slippage_percent: slippagePercent,
        max_trades_to_copy: parseInt(maxTradesToCopy),
        copy_buys: copyBuys,
        copy_sells: copySells,
        token_whitelist: [],
        token_blacklist: []
      };

      console.log(`📊 Rule data to save:`, ruleData);
      console.log(`🔍 editingBotId state:`, editingBotId);
      console.log(`🔍 Operation type:`, editingBotId ? 'UPDATE' : 'CREATE');

      if (editingBotId) {
        // Update existing rule
        console.log(`🔄 Updating existing rule with ID: ${editingBotId}`);
        await copyTradeService.updateCopyTradeRule(effectiveWalletAddress, editingBotId, ruleData);
        showNotification(`Updated copy trader: ${newBotName}`, 'success');
      } else {
        // Create new rule
        console.log(`➕ Creating new rule`);
        await copyTradeService.createCopyTradeRule(effectiveWalletAddress, ruleData);
        showNotification(`Created copy trader: ${newBotName}`, 'success');
      }

      // Reload rules
      console.log(`🔄 Reloading copy trader rules...`);
      await loadCopyTraderRules();
      console.log(`✅ Rules reloaded, current count: ${copyTraderRules.length}`);

      // Reset form
      setIsCreatingBot(false);
      setEditingBotId(null);
      setNewBotName('');
      setSourceWalletAddress('');
      setSourceWalletName('');
      setSelectedWalletsWithDebug(new Set());
      setCreationStep('wallet');

    } catch (error) {
      console.error('Error saving copy trader rule:', error);
      showNotification('Failed to save copy trader rule', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Render rules (styled like SniperBot)
  const renderRules = () => (
    <div className="space-y-2">
      {isLoadingRules ? (
        <div className="flex items-center justify-center py-4">
          <Loader size={16} className="text-[#24c35b] animate-spin mr-2" />
          <span className="text-gray-400 text-xs">Loading copy trader rules...</span>
        </div>
      ) : copyTraderRules.length === 0 ? (
        <div className="text-center py-6">
          <Copy size={24} className="text-gray-500 mx-auto mb-2" />
          <p className="text-gray-400 text-xs">No copy trader rules configured</p>
          <p className="text-gray-500 text-xs mt-1">Create your first rule to start copy trading</p>
        </div>
      ) : (
        copyTraderRules.map((rule) => {
          const ruleStats = ruleStatistics.get(rule.id) || { 
            rule_id: rule.id,
            total_trades: 0, 
            successful_trades: 0, 
            failed_trades: 0,
            success_rate: 0,
            total_volume_sol: 0,
            total_profit_loss_sol: 0,
            average_trade_size_sol: 0
          };
          const successRate = ruleStats.success_rate;
          
          return (
            <div
              key={rule.id}
              className={`p-2 rounded border transition-all ${
                rule.isActive 
                  ? 'border-[#24c35b] bg-[#24c35b]/5' 
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-white text-sm font-medium">{rule.name}</h4>
                                          <button
                        onClick={() => handleRuleToggle(rule.id)}
                        className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-all ${
                          rule.isActive
                            ? 'bg-[#24c35b] text-white hover:bg-[#1ea047]'
                            : 'bg-[#24c35b] text-white hover:bg-[#1ea047]'
                        }`}
                      >
                      {rule.isActive ? <Pause size={10} /> : <Play size={10} />}
                      {rule.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                  
                  <div className="text-xs text-gray-600">
                    <div className="mb-1">
                      Source: {rule.sourceWalletAddress.slice(0, 8)}...{rule.sourceWalletAddress.slice(-8)}
                      {rule.sourceWalletName && (
                        <span className="text-[#24c35b] ml-1">({rule.sourceWalletName})</span>
                      )}
                    </div>
                    <div className="space-y-1">
                      <div className="text-black font-mono text-xs">
                        • Trade Size: {rule.tradeSizeValue} {rule.tradeSizeType === 'percentage' ? '%' : 'SOL'} ({rule.tradeSizeType})
                      </div>
                      <div className="text-black font-mono text-xs">
                        • Max Trades: {rule.maxTradesToCopy || 10} | Slippage: {rule.slippagePercent || 35}% | Delay: {rule.copyDelaySeconds || 2}s
                      </div>
                      <div className="text-black font-mono text-xs">
                        • Copy: {rule.copyBuys !== false && rule.copySells !== false ? 'Buys & Sells' : rule.copyBuys !== false ? 'Buys Only' : rule.copySells !== false ? 'Sells Only' : 'None'}
                      </div>
                    </div>
                  </div>

                  {ruleStats.total_trades > 0 && (
                    <div className="mt-1 text-xs">
                      <div className="flex gap-3 text-gray-600">
                        <span>Trades: {ruleStats.total_trades}</span>
                        <span className="text-green-400">Success: {ruleStats.successful_trades}</span>
                        <span className="text-red-400">Failed: {ruleStats.failed_trades}</span>
                        <span>Rate: {successRate.toFixed(1)}%</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-1 ml-2">
                  <button
                    onClick={() => handleEditBot(rule)}
                    className="p-1 text-gray-600 hover:text-black transition-colors"
                    title="Edit rule"
                  >
                    <Edit2 size={12} />
                  </button>
                  <button
                    onClick={() => handleRuleDelete(rule.id)}
                    className="p-1 text-gray-600 hover:text-red-400 transition-colors"
                    title="Delete rule"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  // Render monitor (styled like SniperBot)
  const renderMonitor = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-white text-sm font-semibold">Active Copy Traders</h4>
        <button
          onClick={() => {
            setIsCreatingBot(true);
            setCreationStep('wallet');
            setEditingBotId(null); // Reset editing state for new bot creation
            setNewBotName('');
            setSourceWalletAddress('');
            setSourceWalletName('');
            setSelectedWalletsWithDebug(new Set()); // Clear selected wallets
          }}
          className="px-3 py-1 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] text-xs hover:bg-[#24c35b]/30 transition-all"
        >
          <Plus size={12} className="mr-1 inline" />
          Create New Bot
        </button>
      </div>

      {/* System Status */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded-lg border border-[#24c35b]/20 bg-[#24c35b]/5">
          <div className="text-white text-lg font-bold">{subscribedWalletsRef.current.size}</div>
          <div className="text-gray-400 text-xs">Wallets Tracked</div>
        </div>
        <div className={`p-3 rounded-lg border ${
          isCopyingActive 
            ? 'border-[#24c35b]/40 bg-[#24c35b]/10' 
            : 'border-gray-600/20 bg-gray-600/5'
        }`}>
          <div className={`text-lg font-bold ${
            isCopyingActive ? 'text-[#24c35b]' : 'text-gray-400'
          }`}>
            {isCopyingActive ? 'ACTIVE' : 'STOPPED'}
          </div>
          <div className="text-gray-400 text-xs">
            {isCopyingActive ? 'Copying Trades' : 'No Copy Trading'}
          </div>
        </div>
      </div>

      {/* Copy Trader Rules List */}
      {renderRules()}
    </div>
  );

  // Memoized Transaction Card to prevent unnecessary re-renders (exactly like SniperBot)
  const TransactionCard = React.memo<{ trade: any; index: number }>(({ trade, index }) => {
    const tokenData = trade.token_address ? tokenDataCacheRef.current.get(trade.token_address) : null;
    const tokenImage = tokenData?.token?.image;
    
    return (
      <div
        key={trade.id || index}
        className={`relative p-3 rounded-lg border overflow-hidden ${
          trade.status === 'success' 
            ? 'border-[#24c35b]/40 bg-[#24c35b]/5' 
            : trade.status === 'pending'
            ? 'border-yellow-500/40 bg-yellow-500/5'
            : 'border-red-500/40 bg-red-500/5'
        }`}
      >
        {/* Background overlay to control image opacity */}
        {tokenImage && (
          <div 
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              backgroundImage: `url(${tokenImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
        )}
        
        {/* Content overlay */}
        <div className="relative z-10">
          {/* Status and Transaction Type labels - Top right */}
          <div className="absolute top-0 right-0 flex gap-1">
            {/* Transaction Type Label */}
            <span className={`px-2 py-1 rounded text-[10px] font-medium ${
              trade.transaction_type === 'buy' 
                ? 'bg-green-500/20 text-green-400 border border-green-500/40' 
                : 'bg-red-500/20 text-red-400 border border-red-500/40'
            }`}>
              {(trade.transaction_type || 'buy').toUpperCase()}
            </span>
            
            {/* Status Label */}
            <span className={`px-2 py-1 rounded text-[10px] font-medium ${
              trade.status === 'success' 
                ? 'bg-[#24c35b]/20 text-[#24c35b] border border-[#24c35b]/40' 
                : trade.status === 'pending'
                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'
                : 'bg-red-500/20 text-red-400 border border-red-500/40'
            }`}>
              {trade.status.toUpperCase()}
            </span>
          </div>

          <div className="flex items-center mb-2">
            <div className="flex items-center flex-1">
              <div className="flex flex-col flex-1">
                <div className="flex items-center gap-2">
                  {/* Timer to the left of token name */}
                  <TransactionTimer createdAt={trade.created_at} />
                  <span className="text-white text-xs font-medium">
                    {trade.token_name || trade.token_symbol} ({trade.token_symbol})
                  </span>
                  {trade.token_address && (
                    <button
                      onClick={() => copyWalletAddress(trade.token_address)}
                      className="text-gray-400 hover:text-white transition-colors"
                      title="Copy token address"
                    >
                      {copiedWallets.has(trade.token_address) ? (
                        <Check size={12} className="text-green-400" />
                      ) : (
                        <Copy size={12} />
                      )}
                    </button>
                  )}
                </div>
                
                {/* Token Social Links - Above action buttons */}
                {trade.status === 'success' && trade.token_address && (
                  <TokenInfoDisplay 
                    tokenAddress={trade.token_address} 
                    tokenSymbol={trade.token_symbol || 'Unknown'} 
                  />
                )}
                
                {/* Action Buttons for Successful Trades - Now underneath token name */}
                {trade.status === 'success' && trade.token_address && (
                  <div className="space-y-1 mt-1">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openAdvancedChartsWidget(trade.token_address, trade.token_symbol || 'Unknown', trade.token_name)}
                        className="px-2 py-1 rounded text-[10px] bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/40 transition-all flex items-center"
                        title={`Open Advanced Charts widget for ${trade.token_symbol || 'token'}`}
                      >
                        <TrendingUp size={10} className="mr-1" />
                        Chart
                      </button>
                      <button
                        onClick={() => openTokenInfoWidget(trade.token_address, trade.token_symbol || 'Unknown', trade.token_name)}
                        className="px-2 py-1 rounded text-[10px] bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 border border-purple-500/40 transition-all flex items-center"
                        title={`Open Token Info widget for ${trade.token_symbol || 'token'}`}
                      >
                        <Info size={10} className="mr-1" />
                        Token
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div>
              <span className="text-gray-400">Type:</span>
              <span className={`ml-1 font-medium ${
                trade.transaction_type === 'buy' ? 'text-green-400' : 'text-red-400'
              }`}>
                {(trade.transaction_type || 'buy').toUpperCase()}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Amount:</span>
              <span className="text-white ml-1">{parseFloat(trade.amount).toFixed(3)} SOL</span>
            </div>
            <div>
              <span className="text-gray-400">Source:</span>
              <span className="text-white ml-1 font-mono">
                {trade.source_wallet?.slice(0, 6)}...{trade.source_wallet?.slice(-4)}
              </span>
            </div>
          </div>
          
          {trade.transaction_signature && (
            <div className="mt-2 text-xs flex items-center justify-between">
              <div>
                <span className="text-gray-400">Txn:</span>
                <a
                  href={`https://solscan.io/tx/${trade.transaction_signature}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#24c35b] ml-1 font-mono break-all hover:text-[#2dd865] transition-colors cursor-pointer underline"
                  title="View on Solscan"
                >
                  {trade.transaction_signature.slice(0, 16)}...{trade.transaction_signature.slice(-8)}
                </a>
              </div>
              
              {/* Market info - Same row as transaction, right aligned */}
              {trade.token_address && tokenData && (
                <div>
                  {(() => {
                    const pools = tokenData.pools || [];
                    const primaryPool = pools.find((pool: any) => pool.liquidity?.usd > 0) || pools[0];
                    const marketName = primaryPool?.market || null;
                    
                    if (marketName && marketName !== 'Unknown') {
                      return (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-700/50 text-gray-300 capitalize">
                          {marketName.replace('-', ' ')}
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
          )}
          
          {trade.error_message && (
            <div className="mt-2 text-xs">
              <span className="text-red-400">Error:</span>
              <span className="text-red-300 ml-1">{trade.error_message}</span>
            </div>
          )}
        </div>
      </div>
    );
  });

  // OPTIMIZED: Memoize transaction list to prevent unnecessary re-renders
  const memoizedTransactionList = useMemo(() => {
    if (isLoadingTradeHistory) {
      return (
        <div className="flex items-center justify-center py-4">
          <Loader size={16} className="text-[#24c35b] animate-spin mr-2" />
          <span className="text-gray-400 text-xs">Loading trade history...</span>
        </div>
      );
    }

    if (tradeHistory.length === 0) {
      return (
        <div className="text-center py-8 text-gray-400">
          <History size={24} className="mx-auto mb-2 opacity-50" />
          <p>No copy trades executed yet</p>
        </div>
      );
    }

    return (
      <div className="space-y-2 flex-1 overflow-y-auto">
        {tradeHistory.map((trade, index) => (
          <TransactionCard key={trade.id || `${trade.token_address}-${index}`} trade={trade} index={index} />
        ))}
      </div>
    );
  }, [tradeHistory, isLoadingTradeHistory]);

  // OPTIMIZED: Memoize the entire transactions render to prevent unnecessary re-renders
  const renderTransactions = useMemo(() => (
    <div className="flex flex-col h-full space-y-4">
      <div className="flex items-center justify-between flex-shrink-0">
        <h4 className="text-white text-sm font-semibold">Copy Trade History</h4>
        <button
          onClick={loadTradeHistory}
          className="px-3 py-1 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] text-xs hover:bg-[#24c35b]/30 transition-all"
        >
          <History size={12} className="mr-1 inline" />
          Refresh
        </button>
      </div>
      {memoizedTransactionList}
    </div>
  ), [loadTradeHistory, memoizedTransactionList]);

  // State for close confirmation
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Handle widget close with proper cleanup
  const handleClose = async () => {
    // Check if there are active rules
    const activeRules = copyTraderRules.filter(rule => rule.isActive);
    
    // If there are active rules, show confirmation dialog
    if (activeRules.length > 0) {
      setShowCloseConfirm(true);
      return;
    }
    
    // If no active rules, close immediately
    await performCleanupAndClose();
  };

  // Perform the actual cleanup and close
  const performCleanupAndClose = async () => {
    console.log('🔄 Copy Trader widget closing - performing cleanup...');
    
    try {
      // 1. Deactivate all active copy trader rules
      const activeRules = copyTraderRules.filter(rule => rule.isActive);
      if (activeRules.length > 0) {
        console.log(`🛑 Deactivating ${activeRules.length} active copy trader rule(s)...`);
        
        const effectiveWalletAddress = getEffectiveWalletAddress();
        if (effectiveWalletAddress) {
          // Deactivate all rules in parallel using the new service
          const deactivationPromises = activeRules.map(async (rule) => {
            try {
              await copyTradeService.toggleCopyTradeRule(effectiveWalletAddress, rule.id, false);
              console.log(`✅ Deactivated rule: ${rule.name}`);
            } catch (error) {
              console.error(`Error deactivating rule ${rule.name}:`, error);
            }
          });

          await Promise.all(deactivationPromises);
        }
      }

      // 2. Clean up all WebSocket subscriptions
      console.log('🔕 Cleaning up WebSocket subscriptions...');
      for (const [walletAddress, subscriptions] of walletSubscriptionsRef.current.entries()) {
        subscriptions.forEach(unsubscribe => unsubscribe());
        wsService.leaveRoom(`wallet:${walletAddress}`);
        console.log(`🔕 Unsubscribed from wallet: ${walletAddress}`);
      }
      walletSubscriptionsRef.current.clear();
      subscribedWalletsRef.current.clear();

      // 3. Reset copy trading state
      setIsCopyingActive(false);
      isCopyingActiveRef.current = false;

      // 4. Clear rule counts and execution locks
      ruleBuyCountRef.current.clear();
      ruleSellCountRef.current.clear();
      recentlyCopiedTransactionsRef.current.clear();
      walletRotationRef.current.clear();
      ruleExecutionLockRef.current.clear();

      console.log('✅ Copy Trader cleanup completed successfully');
      
      // Show notification
      if (activeRules.length > 0) {
        showNotification(`Copy Trader stopped and ${activeRules.length} rule(s) deactivated`, 'success');
      }

    } catch (error) {
      console.error('❌ Error during Copy Trader cleanup:', error);
      showNotification('Error stopping Copy Trader', 'error');
    }

    // Close confirmation dialog if open
    setShowCloseConfirm(false);

    // Finally, call the original onClose
    if (onClose) {
      onClose();
    }
  };

  // Main content for widget (homepage styling)
  const content = (
    <div
      className={`w-full h-full flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm ${widgetClassName}`}
    >
      <style>{globalWidgetTextStyles}</style>
      <style>{`
        /* Hide number input arrows */
        input[type=number]::-webkit-outer-spin-button,
        input[type=number]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
      
      {/* Header */}
      <div className="p-3 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#24c35b]"></div>
          <Copy size={18} className="text-[#24c35b]" />
          <h2 className="text-lg font-bold text-black">Copy Trader</h2>
        </div>
        {onClose && (
          <button 
            onClick={handleClose} 
            className="text-gray-500 hover:text-black transition-colors p-1 rounded hover:bg-gray-100"
            title="Close"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 bg-gray-50">
        {!isCreatingBot ? (
          // Normal tabs when not creating a bot
          [
            { key: 'monitor', label: 'Monitor', icon: Activity },
            { key: 'transactions', label: 'Transactions', icon: History }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-all border-b-2 ${
                activeTab === key
                  ? 'border-[#24c35b] text-[#24c35b] bg-white'
                  : 'border-transparent text-gray-600 hover:text-black hover:bg-gray-100'
              }`}
            >
              <Icon size={16} className="mr-2 inline" />
              {label}
            </button>
          ))
        ) : (
          // Creation flow tabs
          <div className="flex w-full">
            <div className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 ${
              creationStep === 'wallet' 
                ? 'border-[#24c35b] text-[#24c35b] bg-white'
                : 'border-transparent text-gray-600'
            }`}>
              <Target size={16} className="mr-2 inline" />
              1. Source Wallet
            </div>
            <div className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 ${
              creationStep === 'config' 
                ? 'border-[#24c35b] text-[#24c35b] bg-white'
                : 'border-transparent text-gray-600'
            }`}>
              <Settings size={16} className="mr-2 inline" />
              2. Configure
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-grow overflow-y-auto px-4 py-3" style={{ background: '#ffffff', borderRadius: 0, overflowX: 'visible' }}>
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <Loader size={16} className="text-[#24c35b] animate-spin mr-2" />
            <span className="text-gray-400 text-xs">Initializing copy trader...</span>
          </div>
        )}

        {/* Tab Content */}
        {!isLoading && (
          <>
            {!isCreatingBot ? (
              // Normal tab content
              <>
                {activeTab === 'monitor' && renderMonitor()}
                {activeTab === 'transactions' && renderTransactions}
              </>
            ) : (
              // Creation flow content
              <>
                {creationStep === 'wallet' && renderSourceWalletInput()}
                {creationStep === 'config' && renderConfigCreation()}
              </>
            )}
          </>
        )}
      </div>

      {/* Close Confirmation Dialog */}
      {showCloseConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#16161a] border border-yellow-500/20 rounded-lg w-full max-w-md shadow-xl">
            {/* Header */}
            <div className="p-4 border-b border-yellow-500/20 flex items-center justify-between">
              <h3 className="text-white text-lg font-semibold flex items-center">
                <AlertCircle size={20} className="text-yellow-400 mr-2" />
                Close Copy Trader
              </h3>
            </div>

            {/* Content */}
            <div className="p-4">
              <div className="mb-4 p-3 rounded-lg border border-yellow-500/40 bg-yellow-500/10">
                <div className="flex items-center mb-2">
                  <AlertCircle size={16} className="text-yellow-400 mr-2" />
                  <span className="text-yellow-400 font-medium text-sm">Active Copy Traders Warning</span>
                </div>
                <p className="text-yellow-300 text-xs">
                  You have {copyTraderRules.filter(rule => rule.isActive).length} active copy trader rule(s). 
                  Closing this widget will automatically deactivate all rules and stop copy trading.
                </p>
              </div>

              <p className="text-white text-sm mb-2">
                Are you sure you want to close the Copy Trader widget?
              </p>
              
              <div className="text-gray-400 text-xs space-y-1">
                <p>• All active copy trader rules will be deactivated</p>
                <p>• WebSocket subscriptions will be stopped</p>
                <p>• No more trades will be copied automatically</p>
                <p>• You can reopen the widget later to reactivate rules</p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-yellow-500/20 flex items-center justify-end space-x-2">
              <button
                onClick={() => setShowCloseConfirm(false)}
                className="px-4 py-2 rounded-lg border border-gray-600/50 text-gray-400 hover:text-white hover:border-gray-500 transition-all text-sm"
              >
                Cancel
              </button>
              <button
                onClick={performCleanupAndClose}
                className="px-4 py-2 rounded-lg bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/30 transition-all text-sm flex items-center"
              >
                <X size={14} className="mr-1" />
                Close & Stop All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && ruleToDelete && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#16161a] border border-red-500/20 rounded-lg w-full max-w-md shadow-xl">
            {/* Header */}
            <div className="p-4 border-b border-red-500/20 flex items-center justify-between">
              <h3 className="text-white text-lg font-semibold flex items-center">
                <Trash2 size={20} className="text-red-400 mr-2" />
                Delete Copy Trader
              </h3>
            </div>

            {/* Content */}
            <div className="p-4">
              {/* Active bot warning */}
              {ruleToDelete.isActive && (
                <div className="mb-4 p-3 rounded-lg border border-yellow-500/40 bg-yellow-500/10">
                  <div className="flex items-center mb-2">
                    <AlertCircle size={16} className="text-yellow-400 mr-2" />
                    <span className="text-yellow-400 font-medium text-sm">Active Bot Warning</span>
                  </div>
                  <p className="text-yellow-300 text-xs">
                    This bot is currently active and monitoring trades. Deleting it will immediately stop all copying.
                  </p>
                </div>
              )}

              <p className="text-white text-sm mb-2">
                Are you sure you want to delete the copy trader:
              </p>
              <p className="text-[#24c35b] font-semibold text-lg mb-4">
                "{ruleToDelete.name}"
              </p>
              <p className="text-gray-400 text-xs">
                This action cannot be undone and will permanently remove the bot and all its configuration.
              </p>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-red-500/20 flex items-center justify-end space-x-2">
              <button
                onClick={cancelRuleDelete}
                className="px-4 py-2 rounded-lg border border-gray-600/50 text-gray-400 hover:text-white hover:border-gray-500 transition-all text-sm"
              >
                Cancel
              </button>
              <button
                onClick={confirmRuleDelete}
                className="px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30 transition-all text-sm flex items-center"
              >
                <Trash2 size={14} className="mr-1" />
                Delete Bot
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // If this is being rendered as a widget, return just the content
  if (isWidget) {
    return (
      <div className="absolute inset-0 m-0 p-0 overflow-hidden">{content}</div>
    );
  }

  // Otherwise, return the modal version with container
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`bg-[#16161a] border border-gray-700/20 rounded-lg p-6 max-w-md w-full ${widgetClassName}`}>
        {content}
      </div>
    </div>
  );
};

export default CopyTrader;

// Export the widget factory function
export function createCopyTraderWidget(onAddWidget?: (widget: any) => void) {
  return {
    type: "copy-trader-widget",
    title: "Copy Trader",
    content: <CopyTrader isWidget={true} onAddWidget={onAddWidget} />,
    size: { width: 600, height: 600 },
    contentStyle: { 
      padding: 0,
      overflow: "hidden",
    },
    glassMorphism: true,
    containerStyle: {
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(12px)",
      borderRadius: "12px",
      border: "1px solid rgba(36, 195, 91, 0.2)",
      boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(36, 195, 91, 0.2)",
      overflow: "hidden",
    },
  };
} 